/**
 * Customer360Panel — 2-column agent-facing Customer 360.
 *
 * Left column (sticky): Profile card, quick stats, AI insights, merged profiles
 * Right column (scrollable): Cases, orders, assets with progressive disclosure
 *
 * Designed for BPO agents handling live calls/chats in Amazon Connect.
 */
import { useEffect, useMemo, useState } from 'react';
import { CaseLink, CustomerProfileLink, TrackingLink } from './CaseLink';
import { CaseSummary } from './CaseSummary';
import type { CustomerProfile } from './Customer360.types';
import {
  type AssetItem,
  type CaseItem,
  type CookViewItem,
  type MergedCustomer360,
  type OrderItem,
  type ShipmentItem,
  toViewModel,
} from './Customer360.viewmodel';
import { DateTimeDisplay, formatDateTime } from './DateTimeDisplay';

// Inline SVG replacements for the lucide-react icons used by EventIcon.
// Match lucide's prop surface (size, strokeWidth, color) so the existing
// call site is unchanged.
type IconProps = { size?: number; strokeWidth?: number; color?: string };

// Sidebar side — persisted per-user so agents can dock the sidebar
// wherever it's least in the way of their other windows.
type SidebarPosition = 'left' | 'right';
const SIDEBAR_POSITION_STORAGE_KEY = 'c360:sidebar-position';
function readSidebarPosition(): SidebarPosition {
  if (typeof window === 'undefined') return 'left';
  try {
    const raw = window.localStorage.getItem(SIDEBAR_POSITION_STORAGE_KEY);
    return raw === 'right' ? 'right' : 'left';
  } catch {
    return 'left';
  }
}

function MessageSquare({ size = 14, strokeWidth = 2.25, color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function Package({ size = 14, strokeWidth = 2.25, color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16.5 9.4l-9-5.19" />
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  );
}

function ShoppingCart({ size = 14, strokeWidth = 2.25, color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
    </svg>
  );
}

function Wifi({ size = 14, strokeWidth = 2.25, color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.55a11 11 0 0114.08 0" />
      <path d="M1.42 9a16 16 0 0121.16 0" />
      <path d="M8.53 16.11a6 6 0 016.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

// Generic price-tag icon for product (asset) rows. The raw tag path
// occupies roughly x=2..20.59 of the 24-wide viewBox, so the default
// render leaves more empty space on the right than on the left — wrap
// the whole shape in a small translate so it sits centered in the
// timeline/records dot. Distinct from Package (shipment) and
// ShoppingCart (order) at the small size used in the dots.
function ProductTag({ size = 14, strokeWidth = 2.25, color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g transform="translate(0.7 0.7)">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </g>
    </svg>
  );
}

// Inline copy-to-clipboard button. Used wherever an opaque identifier
// (e.g. AC profile UUID) is rendered truncated and the agent needs the
// full value pasted into another tool.
function copyToClipboard(text: string): void {
  // Try modern clipboard API first, fall back to execCommand for iframe compatibility
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      execCommandCopy(text);
    });
  } else {
    execCommandCopy(text);
  }
}

function execCommandCopy(text: string): void {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={label ? `Copy ${label}` : 'Copy'}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 2px',
        cursor: 'pointer',
        color: copied ? '#22c55e' : '#6b7280',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {copied ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  );
}

// Shared detail-row renderer: label + value pair, optionally with a copy
// button sitting immediately to the right of the value text (not at the
// row's trailing edge). `valueStyle` overrides the default value style
// (used for rows where the value is an opaque monospace identifier,
// etc.). Copy text defaults to `value` when it's a string.
function DetailRow({
  label,
  value,
  copy,
  valueStyle,
  multiline,
}: {
  label: string;
  value: React.ReactNode;
  copy?: string;
  valueStyle?: React.CSSProperties;
  /** Allow the value text to wrap to multiple lines (default: single
   *  line with ellipsis). Use for values that can exceed the available
   *  width and you'd rather wrap than truncate — e.g. case UUIDs. */
  multiline?: boolean;
}) {
  const copyText = copy ?? (typeof value === 'string' ? value : undefined);
  const innerTextStyle: React.CSSProperties = multiline
    ? { whiteSpace: 'normal', wordBreak: 'break-all' }
    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      {/* value-group: sizes to content (no flex grow) so the button
          hugs the text. Inner text truncates with ellipsis — or wraps
          if `multiline` — if the row is narrow. */}
      <span
        style={{
          color: c.text,
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: '4px',
          minWidth: 0,
          flex: '0 1 auto',
          ...valueStyle,
        }}
      >
        <span style={innerTextStyle}>{value}</span>
        {copyText && <CopyButton text={copyText} label={label} />}
      </span>
    </div>
  );
}

// =============================================================================
// Filter chip helpers — shared by Cases / Shipments / (future) other tabs
// so the filter UI looks identical everywhere. Mirrors the chip vocabulary
// the Search List layout uses (segmented status pill + boolean toggles +
// sort flip), translated to inline styles consistent with the rest of
// this file.
// =============================================================================

const filterColors = {
  border: '#e5e7eb',
  borderActive: '#3b82f6',
  bg: '#ffffff',
  bgActive: '#eff6ff',
  text: '#1d4ed8',
  textMuted: '#6b7280',
};

interface FilterPillOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

function FilterPillGroup<T extends string>({
  options,
  active,
  onChange,
}: {
  options: FilterPillOption<T>[];
  active: T;
  onChange: (next: T) => void;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        border: `1px solid ${filterColors.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
        fontSize: '11px',
      }}
    >
      {options.map((opt) => {
        const isActive = active === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '3px 10px',
              border: 'none',
              background: isActive ? '#f3f4f6' : 'transparent',
              color: isActive ? '#111827' : filterColors.textMuted,
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              textTransform: 'capitalize',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {opt.label}
            {opt.count != null && (
              <span style={{ fontWeight: 400, opacity: 0.65 }}>{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FilterToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: '11px',
        padding: '3px 8px',
        borderRadius: '6px',
        border: `1px solid ${active ? filterColors.borderActive : filterColors.border}`,
        background: active ? filterColors.bgActive : filterColors.bg,
        color: active ? filterColors.text : filterColors.textMuted,
        cursor: 'pointer',
        fontWeight: active ? 600 : 500,
      }}
    >
      {children}
    </button>
  );
}

function SortToggle({ order, onToggle }: { order: 'newest' | 'oldest'; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        fontSize: '11px',
        padding: '3px 8px',
        borderRadius: '6px',
        border: `1px solid ${filterColors.border}`,
        background: filterColors.bg,
        color: filterColors.textMuted,
        cursor: 'pointer',
      }}
    >
      {order === 'newest' ? '↓ Newest' : '↑ Oldest'}
    </button>
  );
}

// Inline sub-filter row used by the Timeline for per-type narrow-down
// controls (case / shipment status + "has X" toggles). Flat layout — no
// container box, always expanded — so the row sits alongside the type
// chips without adding visual weight.
function TimelineSubFilterPanel({
  label,
  accent,
  children,
}: {
  label: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 4px',
          color: accent,
          fontSize: '11px',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function FilterBar({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        borderBottom: `1px solid ${filterColors.border}`,
        background: '#ffffff',
      }}
    >
      {children}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(d: string): string {
  // `new Date("YYYY-MM-DD")` parses as UTC midnight, then renders as the
  // previous day in any timezone west of UTC. Construct local midnight for
  // date-only inputs so the calendar date survives.
  let parsed: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    parsed = new Date(y, m - 1, day);
  } else {
    parsed = new Date(d);
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(d: string): string {
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// `formatDateTime` is imported from DateTimeDisplay so all three layouts
// render timestamps identically. Use the `<DateTimeDisplay>` component
// (also exported from there) for the common JSX case.

/** Collapse markdown/whitespace for a single-line preview. */
function oneLine(s: string): string {
  return s
    .replace(/\*\*/g, '')
    .replace(/[#_`]/g, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' · ');
}

function relativeDate(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** `Apr 14, 2026 · Today` — absolute date + relative in one string. */
function timeAgo(d: string): string {
  return `${formatDate(d)} · ${relativeDate(d)}`;
}

// =============================================================================
// Styles
// =============================================================================

// Palette tuned for "feels at home in Amazon Connect Agent Workspace".
// Neutrals come from Cloudscape's light-theme tokens (cool slates, not
// the warmer Calltree beige) so the panel sits comfortably inside the
// Connect frame. Status colors stay close to the Calltree design
// system because Cloudscape uses similar muted hues for them.
const c = {
  border: '#ebebf0', //          neutral-250 (Cloudscape divider)
  borderLight: '#f2f3f3', //     neutral-200
  bg: '#ffffff',
  bgMuted: '#f6f6f9', //         light-palette-canvas-pagebackground
  text: '#16191f', //            neutral-950 light (body text)
  textSecondary: '#424650', //   neutral-650
  textMuted: '#545b64', //       neutral-650 alt
  blue: '#006ce0', //            Cloudscape primary link
  bgAccent: '#f0fbff', //        Cloudscape accent surface
  green: '#037f0c', //           Cloudscape success
  red: '#d13212', //             Cloudscape error
  amber: '#8c3b00', //           Cloudscape warning
};

const s: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    display: 'flex',
    // `row-reverse` keeps the existing JSX order (tabbed content written
    // first, sidebar second) but flips their visual order — the sidebar
    // now sits on the LEFT (team decision per c360-UI-2026_04_17 notes).
    // Avoids rewriting the ~300-line JSX block.
    flexDirection: 'row-reverse',
    height: '100%',
    background: c.bgMuted,
    overflow: 'hidden',
  },
  // -- Sidebar column (profile / contacts / stats / products) --
  //   Ordered first in the JSX and, with flex-direction: row, also sits
  //   first visually — i.e. the LEFT side of the screen (team moved this
  //   over from the right per the c360-UI-2026_04_17 notes so the
  //   registered-products + warranty status is the first thing agents
  //   see). The border lives on the RIGHT edge to split it from the
  //   tabbed content.
  left: {
    width: '280px',
    flexShrink: 0,
    borderRight: `1px solid ${c.border}`,
    background: c.bg,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  // -- Tabbed content column --
  right: {
    flex: 1,
    overflow: 'auto',
    // Design-system --bg2 (#f3f3f1) — muted warm surface behind the
    // Timeline / Records / Cooks tab content. Cards sit on top as
    // plain white so the contrast picks them out.
    background: c.bgMuted,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  // -- Profile card --
  profileCard: {
    padding: '16px',
    borderBottom: `1px solid ${c.border}`,
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: c.blue,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    flexShrink: 0,
  },
  profileName: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.text,
    margin: 0,
  },
  profileMeta: {
    fontSize: '11px',
    color: c.textMuted,
  },
  contactList: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: c.textSecondary,
  },
  contactIcon: {
    width: '14px',
    height: '14px',
    color: c.textMuted,
    flexShrink: 0,
  },
  contactValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // -- Stats grid --
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${c.border}`,
  },
  statCell: {
    padding: '10px 16px',
    borderRight: `1px solid ${c.borderLight}`,
    borderBottom: `1px solid ${c.borderLight}`,
  },
  statValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: c.text,
  },
  statLabel: {
    fontSize: '10px',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginTop: '1px',
  },
  // -- Merged profiles --
  mergedSection: {
    padding: '12px 16px',
    borderBottom: `1px solid ${c.border}`,
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  mergedChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 0',
    fontSize: '11px',
    color: c.textSecondary,
  },
  mergedDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: c.blue,
    flexShrink: 0,
  },
  mergedId: {
    fontFamily: 'monospace',
    fontSize: '10px',
    color: c.textMuted,
  },
  // -- Right column cards --
  card: {
    background: c.bg,
    borderRadius: '8px',
    border: `1px solid ${c.border}`,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  cardTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: c.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  cardCount: {
    fontSize: '11px',
    color: c.textMuted,
    fontWeight: 400,
  },
  // -- Case row --
  itemRow: {
    padding: '10px 14px',
    borderTop: `1px solid ${c.borderLight}`,
    transition: 'background 0.1s',
  },
  itemRowExpanded: {
    background: c.bgMuted,
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: 500,
    // Primary text (--fg1 / #3b3b3b) — matches Rob's reference where
    // the timeline row titles use the primary fg, not the accent blue.
    color: c.text,
    lineHeight: 1.3,
    // Allow long titles (case names, product descriptions) to wrap to a
    // second line instead of pushing the row past the viewport.
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  itemMeta: {
    fontSize: '11px',
    color: c.textMuted,
    marginTop: '2px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  itemHeaderDate: {
    fontSize: '10px',
    // Per c360-UI-2026_04_17 notes: dates were too easy to miss in gray.
    // Switch to the primary text color so the event timestamp pops.
    color: c.text,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  itemPreview: {
    fontSize: '11px',
    // Design-system secondary text (--fg2). Matches the supporting
    // line tone in Rob's reference and the Salesforce screenshot.
    color: c.textSecondary,
    marginTop: '6px',
    lineHeight: 1.35,
    overflow: 'hidden',
    // Single-line ellipsis via line-clamp instead of `nowrap` — `nowrap`
    // forces the parent flex chain wide enough to fit the full text on
    // one line, even with `overflow: hidden` higher up. `-webkit-line-clamp`
    // with `display: -webkit-box` gives the same one-line truncation
    // without inflating the parent's intrinsic width.
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  itemDetail: {
    marginTop: '8px',
    padding: '8px 10px',
    background: c.bg,
    borderRadius: '6px',
    border: `1px solid ${c.border}`,
    fontSize: '12px',
  },
  itemProperties: {
    marginTop: '8px',
    marginLeft: '-10px',
    marginRight: '-10px',
    marginBottom: '-8px',
    padding: '8px 10px',
    borderTop: `1px solid ${c.borderLight}`,
    background: c.bgMuted,
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    padding: '3px 0',
  },
  detailLabel: {
    color: c.textMuted,
    flex: '0 0 auto',
    fontSize: '11px',
  },
  detailValue: {
    color: c.text,
    fontWeight: 500,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // -- Order row --
  orderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  orderNum: {
    fontSize: '13px',
    fontWeight: 600,
    color: c.text,
    minWidth: '90px',
  },
  orderDate: {
    fontSize: '11px',
    color: c.textMuted,
    flex: 1,
  },
  orderTotal: {
    fontSize: '13px',
    fontWeight: 600,
    color: c.text,
    textAlign: 'right',
    minWidth: '70px',
  },
  // -- Asset row --
  assetName: {
    fontSize: '13px',
    fontWeight: 500,
    color: c.text,
  },
  assetSerial: {
    fontSize: '10px',
    fontFamily: 'monospace',
    color: c.textMuted,
    marginTop: '1px',
  },
  // -- Badge --
  badge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
  },
  // -- Alert --
  alert: {
    padding: '8px 12px',
    fontSize: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    lineHeight: 1.4,
  },
};

const badgeColors: Record<string, React.CSSProperties> = {
  open: { background: '#fef3c7', color: '#92400e' },
  closed: { background: '#f3f4f6', color: '#6b7280' },
  pending: { background: '#dbeafe', color: '#1e40af' },
  Delivered: { background: '#dcfce7', color: '#166534' },
  Shipped: { background: '#dbeafe', color: '#1e40af' },
  Processing: { background: '#fef3c7', color: '#92400e' },
  Invoiced: { background: '#dcfce7', color: '#166534' },
  Cancelled: { background: '#fef2f2', color: '#991b1b' },
  Active: { background: '#dcfce7', color: '#166534' },
  Inactive: { background: '#f3f4f6', color: '#6b7280' },
  'In Warranty': { background: '#dcfce7', color: '#166534' },
  'Warranty Expired': { background: '#f3f4f6', color: '#6b7280' },
  'Warranty Voided': { background: '#fef2f2', color: '#991b1b' },
};

const alertColors: Record<string, React.CSSProperties> = {
  warning: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
  error: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' },
  info: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
};

// =============================================================================
// Sub-components
// =============================================================================

function Badge({ text }: { text: string }) {
  const colors = badgeColors[text] || { background: '#f3f4f6', color: '#6b7280' };
  return <span style={{ ...s.badge, ...colors }}>{text}</span>;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c.textMuted}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: 'transform 0.15s', transform: open ? 'none' : 'rotate(-90deg)' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      style={s.contactIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      style={s.contactIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      style={s.contactIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// =============================================================================
// Collapsible Section
// =============================================================================

function Section({
  title,
  count,
  defaultOpen = true,
  children,
  badge,
  hideHeader = false,
  isOpen,
  onToggleOpen,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
  hideHeader?: boolean;
  /** Controlled open state. When provided alongside `onToggleOpen`, Section
   *  stops using its local state. Lets the Panel force a section open when
   *  the user clicks into it from the sidebar. */
  isOpen?: boolean;
  onToggleOpen?: () => void;
}) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = isOpen ?? localOpen;
  const handleToggle = onToggleOpen ?? (() => setLocalOpen((v) => !v));
  if (hideHeader) {
    return <div style={s.card}>{children}</div>;
  }
  return (
    <div style={s.card}>
      <div style={s.cardHeader} onClick={handleToggle}>
        <h3 style={s.cardTitle}>
          {title}
          {badge}
          <span style={s.cardCount}>{count}</span>
        </h3>
        <Chevron open={open} />
      </div>
      {open && children}
    </div>
  );
}

// =============================================================================
// Cases Section
// =============================================================================

export function CasesSection({
  cases,
  hideHeader,
  expandedItems,
  toggleItem,
  isOpen,
  onToggleOpen,
}: {
  cases: CaseItem[];
  hideHeader?: boolean;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed' | 'pending'>('all');
  const [hasOrderOnly, setHasOrderOnly] = useState(false);
  const [hasLoupeOnly, setHasLoupeOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const filtered = useMemo(() => {
    const dir = sortOrder === 'newest' ? -1 : 1;
    let result = [...cases].sort(
      (a, b) => dir * (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
    );
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (hasOrderOnly) result = result.filter((c) => c.orderNumber);
    if (hasLoupeOnly) result = result.filter((c) => c.loupeLink);
    return result;
  }, [cases, statusFilter, hasOrderOnly, hasLoupeOnly, sortOrder]);

  const counts = {
    all: cases.length,
    open: cases.filter((c) => c.status === 'open').length,
    closed: cases.filter((c) => c.status === 'closed').length,
    pending: cases.filter((c) => c.status === 'pending').length,
  };

  // Item-expansion state lives on the Panel so it persists across tab
  // switches and stays in sync with the same case shown in Timeline.
  const isOpenById = (id: string) => expandedItems.has(`case:${id}`);
  const toggle = (id: string) => toggleItem(`case:${id}`);

  const statusOptions = [
    { value: 'all' as const, label: 'All', count: counts.all },
    { value: 'open' as const, label: 'Open', count: counts.open },
    { value: 'closed' as const, label: 'Closed', count: counts.closed },
    ...(counts.pending > 0
      ? [{ value: 'pending' as const, label: 'Pending', count: counts.pending }]
      : []),
  ];

  return (
    <Section
      title="Cases"
      count={cases.length}
      hideHeader={hideHeader}
      // Collapsed by default in Records — the count + "N open" badge is
      // enough at a glance; the agent expands when they need to read case
      // details. Panel can force-open via the controlled isOpen prop
      // (e.g. clicking "Open Cases" in the sidebar).
      defaultOpen={false}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
      badge={
        counts.open > 0 ? (
          <span style={{ ...s.badge, background: '#fef3c7', color: '#92400e' }}>
            {counts.open} open
          </span>
        ) : undefined
      }
    >
      <FilterBar>
        <FilterPillGroup options={statusOptions} active={statusFilter} onChange={setStatusFilter} />
        <FilterToggle active={hasOrderOnly} onClick={() => setHasOrderOnly((v) => !v)}>
          Has order
        </FilterToggle>
        <FilterToggle active={hasLoupeOnly} onClick={() => setHasLoupeOnly((v) => !v)}>
          Has Loupe
        </FilterToggle>
        <SortToggle
          order={sortOrder}
          onToggle={() => setSortOrder((v) => (v === 'newest' ? 'oldest' : 'newest'))}
        />
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: filterColors.textMuted }}>
          {filtered.length} / {cases.length}
        </span>
      </FilterBar>
      {filtered.length === 0 && (
        <div
          style={{
            padding: '14px',
            fontSize: '12px',
            color: filterColors.textMuted,
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No cases match these filters.
        </div>
      )}
      {filtered.map((cs) => {
        const isOpen = isOpenById(cs.caseId);
        return (
          <div
            key={cs.caseId}
            className="c360-hover-row"
            style={{
              ...s.itemRow,
              ...(isOpen ? s.itemRowExpanded : {}),
              display: 'flex',
              gap: '8px',
            }}
          >
            <div
              onClick={() => toggle(cs.caseId)}
              style={{
                flexShrink: 0,
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Chevron open={isOpen} />
            </div>
            <div
              onClick={() => toggle(cs.caseId)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                flexShrink: 0,
                background: eventDotColors.case,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <EventIcon type="case" />
            </div>
            <div style={{ flex: 1, minWidth: 0, minHeight: '36px' }}>
              <div style={{ cursor: 'pointer' }} onClick={() => toggle(cs.caseId)}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <div style={{ ...s.itemTitle, flex: 1, minWidth: 0 }}>{cs.title}</div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      paddingTop: '2px',
                      fontSize: '10px',
                    }}
                  >
                    <Badge text={cs.status} />
                    {cs.brand && <Badge text={`Brand: ${cs.brand}`} />}
                    <CaseLink caseId={cs.caseId} style={{ fontSize: '10px' }}>
                      #{cs.referenceNumber}
                    </CaseLink>
                    <span style={s.itemHeaderDate}>{timeAgo(cs.createdAt)}</span>
                  </div>
                </div>
                {!isOpen && cs.summary && <div style={s.itemPreview}>{oneLine(cs.summary)}</div>}
              </div>
              {isOpen && (
                <div style={s.itemDetail} onClick={(e) => e.stopPropagation()}>
                  {cs.summary && (
                    <CaseSummary
                      source={cs.summary}
                      style={{ marginBottom: '6px', color: c.textSecondary }}
                    />
                  )}
                  {cs.productsOrdered && (
                    <div style={{ marginTop: '4px' }}>
                      <span style={s.detailLabel}>Parts ordered</span>
                      {cs.productsOrdered.split(';').map((part, i) => (
                        <div
                          key={i}
                          style={{ padding: '1px 0', color: c.textSecondary, fontSize: '11px' }}
                        >
                          • {part.trim()}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Customer-facing notes + internal comments — these are
                      separate text fields from `summary` and Search List
                      surfaces them prominently. Rendered via CaseSummary
                      so markdown (links, lists, headings) renders the
                      same as the main summary body. */}
                  {cs.customerFacingNotes && (
                    <div style={{ marginTop: '6px' }}>
                      <span style={s.detailLabel}>Customer notes</span>
                      <CaseSummary
                        source={cs.customerFacingNotes}
                        style={{
                          fontSize: '11px',
                          color: c.textSecondary,
                          marginTop: '2px',
                        }}
                      />
                    </div>
                  )}
                  {cs.internalComments && (
                    <div style={{ marginTop: '6px' }}>
                      <span style={s.detailLabel}>Internal comments</span>
                      <CaseSummary
                        source={cs.internalComments}
                        style={{
                          fontSize: '11px',
                          color: c.textSecondary,
                          marginTop: '2px',
                        }}
                      />
                    </div>
                  )}
                  <div style={s.itemProperties}>
                    {cs.asset && <DetailRow label="Product" value={cs.asset} />}
                    {cs.itemSku && (
                      <DetailRow
                        label="Item SKU"
                        value={cs.itemSku}
                        valueStyle={{ fontFamily: 'monospace' }}
                      />
                    )}
                    {cs.channel && <DetailRow label="Channel" value={cs.channel} />}
                    {cs.caseReason && <DetailRow label="Reason" value={cs.caseReason} />}
                    {cs.caseRecordType && (
                      <DetailRow label="Record type" value={cs.caseRecordType} />
                    )}
                    {cs.escalateToQueue && (
                      <DetailRow label="Escalated to" value={cs.escalateToQueue} />
                    )}
                    {cs.agentName && <DetailRow label="Agent" value={cs.agentName} />}
                    {cs.bpo && <DetailRow label="BPO" value={cs.bpo} />}
                    {cs.site && <DetailRow label="Site" value={cs.site} />}
                    {cs.serviceOrderNumber && (
                      <DetailRow
                        label="Service order #"
                        value={cs.serviceOrderNumber}
                        valueStyle={{ fontFamily: 'monospace' }}
                      />
                    )}
                    {cs.orderNumber && (
                      <DetailRow
                        label="Order #"
                        value={cs.orderNumber}
                        valueStyle={{ fontFamily: 'monospace' }}
                      />
                    )}
                    {cs.trackingNumber && (
                      <DetailRow
                        label="Tracking #"
                        value={
                          <TrackingLink trackingNumber={cs.trackingNumber}>
                            {cs.trackingNumber}
                          </TrackingLink>
                        }
                        copy={cs.trackingNumber}
                      />
                    )}
                    <DetailRow
                      label="Created"
                      value={<DateTimeDisplay value={cs.createdAt} />}
                      copy={cs.createdAt}
                    />
                    {cs.lastUpdated && (
                      <DetailRow
                        label="Updated"
                        value={<DateTimeDisplay value={cs.lastUpdated} />}
                        copy={cs.lastUpdated}
                      />
                    )}
                    {cs.closedAt && (
                      <DetailRow
                        label="Closed"
                        value={<DateTimeDisplay value={cs.closedAt} />}
                        copy={cs.closedAt}
                      />
                    )}
                    {cs.reopenedAt && (
                      <DetailRow
                        label="Reopened"
                        value={<DateTimeDisplay value={cs.reopenedAt} />}
                        copy={cs.reopenedAt}
                      />
                    )}
                    {cs.contactId && (
                      <DetailRow
                        label="Contact ID"
                        value={cs.contactId}
                        valueStyle={{ fontFamily: 'monospace' }}
                      />
                    )}
                    <DetailRow
                      label="Case ID"
                      value={cs.caseId}
                      valueStyle={{ fontFamily: 'monospace' }}
                      multiline
                    />
                    {cs.loupeLink && (
                      <DetailRow
                        label="Loupe"
                        value={
                          <a
                            href={cs.loupeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: c.blue, textDecoration: 'underline' }}
                          >
                            Open scan results
                          </a>
                        }
                        copy={cs.loupeLink}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Section>
  );
}

// =============================================================================
// Orders Section
// =============================================================================

export function OrdersSection({
  orders,
  hideHeader,
  expandedItems,
  toggleItem,
  isOpen,
  onToggleOpen,
}: {
  orders: OrderItem[];
  hideHeader?: boolean;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
}) {
  const isOpenById = (id: string) => expandedItems.has(`order:${id}`);
  const toggle = (id: string) => toggleItem(`order:${id}`);

  return (
    <Section
      title="Orders"
      count={orders.length}
      hideHeader={hideHeader}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
    >
      {orders.map((o) => {
        const isOpen = isOpenById(o.orderNumber);
        return (
          <div key={o.orderNumber} style={{ ...s.itemRow, ...(isOpen ? s.itemRowExpanded : {}) }}>
            <div style={{ ...s.orderRow, cursor: 'pointer' }} onClick={() => toggle(o.orderNumber)}>
              <span style={s.orderNum}>
                {o.orderNumber}
                {o.isWarranty && (
                  <span style={{ marginLeft: '4px' }}>
                    <Badge text="Warranty" />
                  </span>
                )}
              </span>
              <span style={s.orderDate}>{timeAgo(o.date)}</span>
              <Badge text={o.status} />
              <span style={s.orderTotal}>{formatCurrency(o.total)}</span>
            </div>
            {isOpen && (
              <div style={s.itemDetail} onClick={(e) => e.stopPropagation()}>
                <DetailRow label="Date" value={<DateTimeDisplay value={o.date} />} copy={o.date} />
                {o.shippingAddress && <DetailRow label="Ship to" value={o.shippingAddress} />}
                {o.items &&
                  o.items.map((item, i) => (
                    <div key={i} style={{ padding: '2px 0', color: c.textSecondary }}>
                      • {item}
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// =============================================================================
// Assets Section
// =============================================================================

export function AssetsSection({
  assets,
  hideHeader,
  expandedItems,
  toggleItem,
  isOpen,
  onToggleOpen,
}: {
  assets: AssetItem[];
  hideHeader?: boolean;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
}) {
  const isOpenById = (id: string) => expandedItems.has(`asset:${id}`);
  const toggle = (id: string) => toggleItem(`asset:${id}`);

  return (
    <Section
      title="Registered Products"
      count={assets.length}
      hideHeader={hideHeader}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
    >
      {assets.map((a) => {
        const isOpen = isOpenById(a.serialNumber);
        return (
          <div
            key={a.serialNumber}
            className="c360-hover-row"
            style={{
              ...s.itemRow,
              ...(isOpen ? s.itemRowExpanded : {}),
              display: 'flex',
              gap: '8px',
            }}
          >
            <div
              onClick={() => toggle(a.serialNumber)}
              style={{
                flexShrink: 0,
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Chevron open={isOpen} />
            </div>
            <div
              onClick={() => toggle(a.serialNumber)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                flexShrink: 0,
                // Match Timeline's asset event icon (grill + red dot) —
                // this row used to share the Order icon from before there
                // was a proper `asset` event type, which made the same
                // product look different between Records and Timeline.
                background: eventDotColors.asset,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <EventIcon type="asset" />
            </div>
            <div style={{ flex: 1, minWidth: 0, minHeight: '36px' }}>
              <div style={{ cursor: 'pointer' }} onClick={() => toggle(a.serialNumber)}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <div style={{ ...s.itemTitle, flex: 1, minWidth: 0 }}>
                    {a.productName}
                    {a.nickname && (
                      <span
                        style={{
                          fontWeight: 400,
                          color: c.textMuted,
                          marginLeft: '6px',
                        }}
                      >
                        — {a.nickname}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      paddingTop: '2px',
                      fontSize: '10px',
                    }}
                  >
                    {a.warrantyVoided ? (
                      <Badge text="Warranty Voided" />
                    ) : a.warrantyPassed ? (
                      <Badge text="Warranty Expired" />
                    ) : a.warrantyExpiration ? (
                      <Badge text="In Warranty" />
                    ) : null}
                    <Badge text={a.status} />
                    {a.registeredDate && (
                      <span style={s.itemHeaderDate}>{timeAgo(a.registeredDate)}</span>
                    )}
                  </div>
                </div>
                {!isOpen && (
                  <div style={s.itemPreview}>
                    SN {a.serialNumber}
                    {a.deviceUuid && a.deviceUuid !== a.serialNumber && ` · UUID ${a.deviceUuid}`}
                    {a.productSku && ` · SKU ${a.productSku}`}
                    {a.productFamily && ` · ${a.productFamily}`}
                  </div>
                )}
              </div>
              {isOpen && (
                <div style={s.itemDetail} onClick={(e) => e.stopPropagation()}>
                  {a.productSku && (
                    <DetailRow
                      label="SKU"
                      value={a.productSku}
                      valueStyle={{ fontFamily: 'monospace' }}
                    />
                  )}
                  <DetailRow
                    label="Serial"
                    value={a.serialNumber}
                    valueStyle={{ fontFamily: 'monospace' }}
                  />
                  {a.deviceUuid && a.deviceUuid !== a.serialNumber && (
                    <DetailRow
                      label="UUID"
                      value={a.deviceUuid}
                      valueStyle={{ fontFamily: 'monospace' }}
                    />
                  )}
                  {a.msrp != null && <DetailRow label="MSRP" value={`$${a.msrp.toFixed(2)}`} />}
                  {a.purchaseDate && (
                    <DetailRow
                      label="Purchased"
                      value={<DateTimeDisplay value={a.purchaseDate} />}
                      copy={a.purchaseDate}
                    />
                  )}
                  {a.purchaseStore && <DetailRow label="Store" value={a.purchaseStore} />}
                  {a.registeredDate && (
                    <DetailRow
                      label="Last Registered"
                      value={<DateTimeDisplay value={a.registeredDate} />}
                      copy={a.registeredDate}
                    />
                  )}
                  {a.warrantyStartDate && (
                    <DetailRow
                      label="Warranty Start"
                      value={<DateTimeDisplay value={a.warrantyStartDate} />}
                      copy={a.warrantyStartDate}
                    />
                  )}
                  {a.warrantyExpiration && (
                    <DetailRow
                      label="Warranty Expires"
                      value={
                        <>
                          {formatDateTime(a.warrantyExpiration)}
                          {a.warrantyVoided && ' (Voided)'}
                        </>
                      }
                      copy={a.warrantyExpiration}
                      valueStyle={{
                        color: a.warrantyPassed ? c.red : c.green,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {a.returnable !== undefined && (
                    <DetailRow label="Returnable" value={a.returnable ? 'Yes' : 'No'} />
                  )}
                  {/* Connectivity / classification — Search List shows
                      these per asset; the agent-app rendering used to
                      drop them silently. Label "Controller" matches
                      Search List wording (the value is the controller
                      family — Kirby, etc). */}
                  {/* TODO: Traeger-specific grill classification rows
                      (controller family, grill line, model). Generalize. */}
                  {a.controllerFamily && (
                    <DetailRow label="Controller" value={a.controllerFamily} />
                  )}
                  {a.grillLine && a.grillLine !== a.categorySeries && (
                    <DetailRow label="Line" value={a.grillLine} />
                  )}
                  {a.modelFamily && a.modelFamily !== a.categoryModel && (
                    <DetailRow label="Model" value={a.modelFamily} />
                  )}
                  {a.firmwareVersion && <DetailRow label="Firmware" value={a.firmwareVersion} />}
                  {a.configVersion && <DetailRow label="Config" value={a.configVersion} />}
                  {a.deviceFriendlyName && (
                    <DetailRow label="Device name" value={a.deviceFriendlyName} />
                  )}
                  {a.wifiName && <DetailRow label="WiFi" value={a.wifiName} />}
                  {a.wifiSignalStrength != null && (
                    <DetailRow label="Signal" value={`${a.wifiSignalStrength} dBm`} />
                  )}
                  {a.lastConnected && (
                    <DetailRow
                      label="Last connected"
                      value={<DateTimeDisplay value={a.lastConnected} />}
                      copy={a.lastConnected}
                    />
                  )}
                  {/* IoT-account / Connected-device fields, surfaced here so
                      Products subsumes the old Connected Devices tab. */}
                  {a.ownerName && <DetailRow label="Owner" value={a.ownerName} />}
                  {a.ownerEmail && <DetailRow label="Owner email" value={a.ownerEmail} />}
                  {a.timeZone && <DetailRow label="Timezone" value={a.timeZone} />}
                  {a.skillLevel && <DetailRow label="Skill" value={a.skillLevel} />}
                  {a.fleetEntryDate && (
                    <DetailRow
                      label="In fleet"
                      value={<DateTimeDisplay value={a.fleetEntryDate} />}
                      copy={a.fleetEntryDate}
                    />
                  )}
                  {a.manufacturedAt && (
                    <DetailRow
                      label="Manufactured"
                      value={<DateTimeDisplay value={a.manufacturedAt} />}
                      copy={a.manufacturedAt}
                    />
                  )}
                  {a.googleMapsLink && (
                    <DetailRow
                      label="Location"
                      value={
                        <a
                          href={a.googleMapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: c.blue, textDecoration: 'underline' }}
                        >
                          Open in Google Maps
                        </a>
                      }
                      copy={a.googleMapsLink}
                    />
                  )}
                  {/* TODO: Traeger-specific — total cooks on an asset. */}
                  {a.totalCooks != null && (
                    <DetailRow label="Total cooks" value={String(a.totalCooks)} />
                  )}
                  {a.isCourtesyItem && <DetailRow label="Courtesy item" value="Yes" />}
                  {a.categoryL1 && (
                    <DetailRow
                      label="Taxonomy"
                      value={[a.categoryL1, a.categoryL2, a.categorySeries, a.categoryModel]
                        .filter(Boolean)
                        .join(' > ')}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Section>
  );
}

// =============================================================================
// Shipments Section
// =============================================================================

export function ShipmentsSection({
  shipments,
  hideHeader,
  expandedItems,
  toggleItem,
  isOpen,
  onToggleOpen,
}: {
  shipments: ShipmentItem[];
  hideHeader?: boolean;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'delivered' | 'shipped' | 'pending'>(
    'all'
  );
  const [hasTrackingOnly, setHasTrackingOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Apply per-shipment filters first, then re-group by order so each order
  // card only contains items that pass the filters. An order with zero
  // matching items drops out entirely — same behavior Search List uses for
  // its flat per-shipment list, just translated to grouped rendering.
  const counts = useMemo(
    () => ({
      all: shipments.length,
      delivered: shipments.filter((s) => s.isDelivered).length,
      shipped: shipments.filter((s) => s.shipDate && !s.isDelivered).length,
      pending: shipments.filter((s) => !s.shipDate).length,
    }),
    [shipments]
  );

  const grouped = useMemo(() => {
    let filteredShipments = [...shipments];
    if (statusFilter === 'delivered') {
      filteredShipments = filteredShipments.filter((s) => s.isDelivered);
    } else if (statusFilter === 'shipped') {
      filteredShipments = filteredShipments.filter((s) => s.shipDate && !s.isDelivered);
    } else if (statusFilter === 'pending') {
      filteredShipments = filteredShipments.filter((s) => !s.shipDate);
    }
    if (hasTrackingOnly) {
      filteredShipments = filteredShipments.filter((s) => s.trackingNumber);
    }
    const groups: Record<string, ShipmentItem[]> = {};
    for (const s of filteredShipments) {
      const key = String(s.orderNumber);
      (groups[key] = groups[key] || []).push(s);
    }
    const dir = sortOrder === 'newest' ? -1 : 1;
    const sortedEntries = Object.entries(groups).sort(([, a], [, b]) => {
      const ad = a[0]?.orderDate ?? '';
      const bd = b[0]?.orderDate ?? '';
      return dir * ad.localeCompare(bd);
    });
    return sortedEntries;
  }, [shipments, statusFilter, hasTrackingOnly, sortOrder]);

  const isOpenById = (id: string) => expandedItems.has(`ship:${id}`);
  const toggle = (id: string) => toggleItem(`ship:${id}`);

  const statusOptions = [
    { value: 'all' as const, label: 'All', count: counts.all },
    { value: 'delivered' as const, label: 'Delivered', count: counts.delivered },
    { value: 'shipped' as const, label: 'Shipped', count: counts.shipped },
    { value: 'pending' as const, label: 'Pending', count: counts.pending },
  ];

  const filteredShipmentCount = grouped.reduce((sum, [, items]) => sum + items.length, 0);

  return (
    <Section
      title="Shipments"
      count={shipments.length}
      hideHeader={hideHeader}
      // Collapsed by default in Records — agents scan the count first and
      // expand when they actually need to confirm what's in transit.
      defaultOpen={false}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
    >
      <FilterBar>
        <FilterPillGroup options={statusOptions} active={statusFilter} onChange={setStatusFilter} />
        <FilterToggle active={hasTrackingOnly} onClick={() => setHasTrackingOnly((v) => !v)}>
          Has tracking
        </FilterToggle>
        <SortToggle
          order={sortOrder}
          onToggle={() => setSortOrder((v) => (v === 'newest' ? 'oldest' : 'newest'))}
        />
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: filterColors.textMuted }}>
          {filteredShipmentCount} / {shipments.length}
        </span>
      </FilterBar>
      {grouped.length === 0 && (
        <div
          style={{
            padding: '14px',
            fontSize: '12px',
            color: filterColors.textMuted,
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No shipments match these filters.
        </div>
      )}
      {grouped.map(([orderNum, items]) => {
        const first = items[0];
        const totalPrice = items.reduce((sum, it) => sum + (it.priceUsd || 0), 0);
        const isOpen = isOpenById(orderNum);
        return (
          <div
            key={orderNum}
            className="c360-hover-row"
            style={{
              ...s.itemRow,
              ...(isOpen ? s.itemRowExpanded : {}),
              display: 'flex',
              gap: '8px',
            }}
          >
            <div
              onClick={() => toggle(orderNum)}
              style={{
                flexShrink: 0,
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Chevron open={isOpen} />
            </div>
            <div
              onClick={() => toggle(orderNum)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                flexShrink: 0,
                background: eventDotColors.shipment,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <EventIcon type="shipment" />
            </div>
            <div style={{ flex: 1, minWidth: 0, minHeight: '36px' }}>
              <div style={{ cursor: 'pointer' }} onClick={() => toggle(orderNum)}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <div style={{ ...s.itemTitle, flex: 1, minWidth: 0 }}>Order #{orderNum}</div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      paddingTop: '2px',
                      fontSize: '10px',
                    }}
                  >
                    {totalPrice > 0 && <Badge text={formatCurrency(totalPrice)} />}
                    {first.sourceSystem && (
                      <span style={{ color: c.textMuted }}>via {first.sourceSystem}</span>
                    )}
                    <span style={s.itemHeaderDate}>{timeAgo(first.orderDate)}</span>
                  </div>
                </div>
                {!isOpen && (
                  <div style={s.itemPreview}>
                    {items.length} item{items.length > 1 ? 's' : ''}:{' '}
                    {items.map((it) => it.productDescription).join(', ')}
                  </div>
                )}
              </div>
              {isOpen && (
                <div style={s.itemDetail} onClick={(e) => e.stopPropagation()}>
                  {items.map((item, i) => {
                    // Mirror the Search List logic: is_delivered → ship_date
                    // → pending. tracking_number is unreliable — sometimes
                    // empty even after shipment (carrier wrote it to a
                    // header field instead).
                    const primaryStatus = item.isDelivered
                      ? 'Delivered'
                      : item.shipDate
                        ? 'Shipped'
                        : 'Pending';
                    const destination = [item.destinationCity, item.destinationState]
                      .filter(Boolean)
                      .join(', ');
                    return (
                      <div
                        key={i}
                        style={{
                          padding: '4px 0',
                          borderTop: i > 0 ? `1px solid ${c.borderLight}` : 'none',
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>
                          <span style={{ color: c.text, fontWeight: 500 }}>
                            {item.productDescription}
                          </span>
                          <span
                            style={{
                              color: c.textMuted,
                              marginLeft: '6px',
                              fontFamily: 'monospace',
                              fontSize: '10px',
                            }}
                          >
                            {item.productSku}
                          </span>
                          {item.priceUsd != null && item.priceUsd > 0 && (
                            <span style={{ color: c.textSecondary, marginLeft: '6px' }}>
                              {formatCurrency(item.priceUsd)}
                            </span>
                          )}
                        </div>
                        <div style={s.itemMeta}>
                          <Badge text={primaryStatus} />
                          {/* Per-item dates — Search List shows
                              Ordered/Shipped/Delivered alongside the item;
                              the Timeline tab anchors a date to every event,
                              so reproduce that here for parity. Use
                              formatDateTime so time + TZ surface when the
                              data carries them. */}
                          {item.shipDate && <span>Shipped {formatDateTime(item.shipDate)}</span>}
                          {item.deliveredDate && (
                            <span>Delivered {formatDateTime(item.deliveredDate)}</span>
                          )}
                          {item.isDeliveredOnTime === false && <Badge text="Late" />}
                          {item.daysLate != null && item.daysLate > 0 && (
                            <span style={{ color: c.red, fontWeight: 600 }}>
                              {item.daysLate}d late
                            </span>
                          )}
                          {item.appointmentChanges != null && item.appointmentChanges > 0 && (
                            <span>
                              {item.appointmentChanges} reschedule
                              {item.appointmentChanges > 1 ? 's' : ''}
                            </span>
                          )}
                          {item.shipperName && <span>{item.shipperName}</span>}
                          {destination && <span>→ {destination}</span>}
                          {item.trackingNumber && (
                            <TrackingLink
                              shipperName={item.shipperName}
                              trackingNumber={item.trackingNumber}
                              style={{ fontFamily: 'monospace' }}
                            >
                              {item.trackingNumber}
                            </TrackingLink>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(first.shippingAddress || first.customerPoNumber || first.paymentTerms) && (
                    <div
                      style={{
                        marginTop: '6px',
                        paddingTop: '6px',
                        borderTop: `1px solid ${c.borderLight}`,
                        fontSize: '11px',
                        color: c.textMuted,
                      }}
                    >
                      {first.shippingAddress && <div>Ship to: {first.shippingAddress}</div>}
                      {first.customerPoNumber && (
                        <div>
                          PO:{' '}
                          <span style={{ fontFamily: 'monospace' }}>{first.customerPoNumber}</span>
                        </div>
                      )}
                      {first.paymentTerms && <div>Payment: {first.paymentTerms}</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Section>
  );
}

// =============================================================================
// Connected Devices Section
// =============================================================================

function daysSince(d: string | null | undefined): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

// =============================================================================
// TODO: Traeger-specific cooks section (grill cook sessions, temperature in
// °F, problem_cook_type labels). The entire CooksSection, CookRow,
// formatTemp, and filter/sort logic below are Traeger-only. Move to tenant
// model-driven rendering when implemented (Phase 2).
// =============================================================================

function formatTemp(celsius: number | null): string | null {
  if (celsius == null) return null;
  const f = Math.round((celsius * 9) / 5 + 32);
  return `${f}°F`;
}

/** Compact labels for problem_cook_type (raw values can be verbose). */
const PROBLEM_COOK_LABEL: Record<string, string> = {
  'Frequent Changes': 'Frequent SP',
  'Small Changes': 'Small SP',
};

function CookRow({
  cook,
  open,
  onToggle,
}: {
  cook: CookViewItem;
  open: boolean;
  onToggle: () => void;
}) {
  const probe =
    cook.probeMin != null && cook.probeMax != null
      ? `${formatTemp(cook.probeMin)}→${formatTemp(cook.probeMax)}`
      : null;
  const problemLabel = cook.problemType
    ? (PROBLEM_COOK_LABEL[cook.problemType] ?? cook.problemType)
    : null;
  const hasIssue = cook.criticalAlerts > 0 || problemLabel;
  const chip = hasIssue
    ? { bg: '#fee2e2', fg: '#991b1b', label: problemLabel ?? 'Alert' }
    : cook.warningAlerts > 0
      ? { bg: '#fef3c7', fg: '#92400e', label: 'Warn' }
      : { bg: '#dcfce7', fg: '#166534', label: 'OK' };
  const rowStyle: React.CSSProperties = {
    fontSize: '11px',
    cursor: 'pointer',
    background: open ? c.bgMuted : 'transparent',
    borderTop: `1px solid ${c.borderLight}`,
  };
  const cellStyle: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'middle' };
  return (
    <>
      <tr className="c360-hover-row" style={rowStyle} onClick={onToggle}>
        <td
          style={{
            ...cellStyle,
            color: c.textMuted,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
          }}
        >
          {cook.startedAt
            ? `${formatDate(cook.startedAt).replace(/, \d{4}$/, '')} ${formatTime(cook.startedAt)}`
            : '—'}
        </td>
        <td style={{ ...cellStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
          <span
            style={{
              display: 'inline-block',
              padding: '1px 6px',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: 600,
              textAlign: 'center',
              background: chip.bg,
              color: chip.fg,
              whiteSpace: 'nowrap',
              minWidth: '70px',
              boxSizing: 'border-box',
            }}
          >
            {chip.label}
          </span>
        </td>
        <td
          style={{
            ...cellStyle,
            color: c.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {cook.durationMinutes != null ? `${cook.durationMinutes}m` : '—'}
          {probe && <span style={{ color: c.textMuted }}> · Probe {probe}</span>}
          {cook.goalTemp != null && (
            <span style={{ color: c.textMuted }}> · Goal {formatTemp(cook.goalTemp)}</span>
          )}
          {cook.criticalAlerts > 0 && (
            <span style={{ color: '#991b1b', fontWeight: 600 }}>
              {' '}
              · {cook.criticalAlerts} critical
            </span>
          )}
          {cook.warningAlerts > 0 && (
            <span style={{ color: '#92400e' }}> · {cook.warningAlerts} warn</span>
          )}
        </td>
      </tr>
      {open && (
        <tr style={{ background: c.bgMuted, borderTop: `1px solid ${c.borderLight}` }}>
          <td
            colSpan={3}
            style={{
              padding: '10px 14px 12px',
              fontSize: '11px',
              color: c.textSecondary,
              lineHeight: 1.6,
            }}
          >
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 16px' }}
            >
              {cook.startedAt && (
                <div>
                  <span style={s.detailLabel}>Started</span>
                  <span style={s.detailValue}>
                    {formatDate(cook.startedAt)} {formatTime(cook.startedAt)}
                  </span>
                </div>
              )}
              {cook.durationMinutes != null && (
                <div>
                  <span style={s.detailLabel}>Duration</span>
                  <span style={s.detailValue}>{cook.durationMinutes} min</span>
                </div>
              )}
              {cook.ambientMin != null && cook.ambientMax != null && (
                <div>
                  <span style={s.detailLabel}>Ambient</span>
                  <span style={s.detailValue}>
                    {formatTemp(cook.ambientMin)} → {formatTemp(cook.ambientMax)}
                  </span>
                </div>
              )}
              {cook.probeMin != null && cook.probeMax != null && (
                <div>
                  <span style={s.detailLabel}>Probe</span>
                  <span style={s.detailValue}>
                    {formatTemp(cook.probeMin)} → {formatTemp(cook.probeMax)}
                  </span>
                </div>
              )}
              {cook.goalTemp != null && (
                <div>
                  <span style={s.detailLabel}>Goal</span>
                  <span style={s.detailValue}>{formatTemp(cook.goalTemp)}</span>
                </div>
              )}
              {cook.criticalAlerts > 0 && (
                <div>
                  <span style={s.detailLabel}>Critical alerts</span>
                  <span style={{ ...s.detailValue, color: '#991b1b', fontWeight: 600 }}>
                    {cook.criticalAlerts}
                  </span>
                </div>
              )}
              {cook.warningAlerts > 0 && (
                <div>
                  <span style={s.detailLabel}>Warnings</span>
                  <span style={{ ...s.detailValue, color: '#92400e' }}>{cook.warningAlerts}</span>
                </div>
              )}
              {cook.lidOpenings > 0 && (
                <div>
                  <span style={s.detailLabel}>Lid opens</span>
                  <span style={s.detailValue}>{cook.lidOpenings}</span>
                </div>
              )}
              {cook.setpointChanges > 0 && (
                <div>
                  <span style={s.detailLabel}>Setpoint changes</span>
                  <span style={s.detailValue}>{cook.setpointChanges}</span>
                </div>
              )}
              {cook.problemType && (
                <div>
                  <span style={s.detailLabel}>Problem</span>
                  <span style={{ ...s.detailValue, color: '#991b1b' }}>{cook.problemType}</span>
                </div>
              )}
              {(cook.deviceFriendlyName || cook.deviceTradeName) && (
                <div>
                  <span style={s.detailLabel}>Device</span>
                  <span style={s.detailValue}>
                    {cook.deviceFriendlyName || cook.deviceTradeName}
                  </span>
                </div>
              )}
              <div>
                <span style={s.detailLabel}>Cook ID</span>
                <span
                  style={{
                    ...s.detailValue,
                    fontFamily: 'monospace',
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: '4px',
                  }}
                >
                  {cook.cookId}
                  <CopyButton text={cook.cookId} label="Cook ID" />
                </span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

type CookFilter = 'all' | 'critical' | 'warnings' | 'problems';
type CookSort = 'newest' | 'oldest' | 'alerts';

const COOK_FILTER_LABELS: Record<CookFilter, string> = {
  all: 'All',
  critical: 'Critical',
  warnings: 'Warnings',
  problems: 'Problems',
};

const COOK_FILTER_COLORS: Record<CookFilter, string> = {
  all: '#6b7280',
  critical: '#ef4444',
  warnings: '#f59e0b',
  problems: '#ef4444',
};

function CooksSection({
  cooks,
  hideHeader,
  filter,
  onFilterChange,
  expandedItems,
  toggleItem,
}: {
  cooks: CookViewItem[];
  hideHeader?: boolean;
  filter: CookFilter;
  onFilterChange: (f: CookFilter) => void;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
}) {
  const isOpenById = (id: string) => expandedItems.has(`cook:${id}`);
  const toggle = (id: string) => toggleItem(`cook:${id}`);

  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [keyword, setKeyword] = useState('');
  const [sortOrder, setSortOrder] = useState<CookSort>('newest');

  const cookCounts: Record<CookFilter, number> = {
    all: cooks.length,
    critical: cooks.filter((c) => c.criticalAlerts > 0).length,
    warnings: cooks.filter((c) => c.warningAlerts > 0).length,
    problems: cooks.filter((c) => c.problemType != null).length,
  };

  const filtered = (() => {
    const cutoff =
      DATE_RANGE_DAYS[dateRange] != null
        ? Date.now() - DATE_RANGE_DAYS[dateRange]! * 86400000
        : null;
    const kw = keyword.trim().toLowerCase();
    const out = cooks.filter((cook) => {
      if (filter === 'critical' && cook.criticalAlerts === 0) return false;
      if (filter === 'warnings' && cook.warningAlerts === 0) return false;
      if (filter === 'problems' && cook.problemType == null) return false;
      if (cutoff && cook.startedAt && new Date(cook.startedAt).getTime() < cutoff) return false;
      if (kw) {
        const hay = [cook.deviceFriendlyName, cook.deviceTradeName, cook.problemType]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
    if (sortOrder === 'oldest') {
      out.sort(
        (a, b) =>
          (a.startedAt ? new Date(a.startedAt).getTime() : 0) -
          (b.startedAt ? new Date(b.startedAt).getTime() : 0)
      );
    } else if (sortOrder === 'alerts') {
      out.sort(
        (a, b) =>
          b.criticalAlerts * 10 + b.warningAlerts - (a.criticalAlerts * 10 + a.warningAlerts)
      );
    }
    return out;
  })();

  const filterKeys: CookFilter[] = ['all', 'critical', 'warnings', 'problems'];

  return (
    <Section title="Cooks" count={filtered.length} hideHeader={hideHeader}>
      <div
        style={{
          padding: '8px 14px',
          borderBottom: `1px solid ${c.borderLight}`,
          background: c.bg,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {filterKeys.map((f) => {
            const isActive = filter === f;
            const color = COOK_FILTER_COLORS[f];
            const count = cookCounts[f];
            const disabled = count === 0;
            return (
              <button
                key={f}
                className="c360-chip"
                disabled={disabled}
                onClick={() => onFilterChange(f)}
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: '10px',
                  border: `1px solid ${isActive ? color : c.border}`,
                  background: isActive ? color + '20' : c.bg,
                  color: disabled ? c.textMuted : isActive ? color : c.textMuted,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {COOK_FILTER_LABELS[f]}
                <span style={{ fontWeight: 400, opacity: 0.7 }}>{count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Filter device, problem…"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{
              flex: '1 1 140px',
              minWidth: '100px',
              fontSize: '11px',
              padding: '4px 8px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              outline: 'none',
            }}
          />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            style={{
              fontSize: '11px',
              padding: '4px 6px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: 'pointer',
            }}
          >
            {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((r) => (
              <option key={r} value={r}>
                {DATE_RANGE_LABELS[r]}
              </option>
            ))}
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as CookSort)}
            style={{
              fontSize: '11px',
              padding: '4px 6px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: 'pointer',
            }}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="alerts">Most alerts</option>
          </select>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr
            style={{
              background: c.bgMuted,
              fontSize: '10px',
              fontWeight: 600,
              color: c.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
            }}
          >
            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>
              Date &amp; time
            </th>
            <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600 }}>Status</th>
            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>Summary</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((cook) => (
            <CookRow
              key={cook.cookId}
              cook={cook}
              open={isOpenById(cook.cookId)}
              onToggle={() => toggle(cook.cookId)}
            />
          ))}
        </tbody>
      </table>
    </Section>
  );
}

// =============================================================================
// Timeline Section (inline, expandable items — no 3rd column)
// =============================================================================

interface TimelineEvent {
  id: string;
  type: 'case' | 'shipment' | 'device' | 'order' | 'asset';
  title: string;
  timestamp: string;
  status?: string;
  brand?: string;
  summary?: string;
  detail: Record<string, unknown>;
}

const eventDotColors: Record<string, string> = {
  case: '#3b82f6',
  shipment: '#8b5cf6',
  order: '#22c55e',
  device: '#f59e0b',
  asset: '#ef4444',
};

// Tonal pill palettes — used by the filter-chip render below when a
// type has a matching "tonal" variant in the Calltree design system
// (bg / border / fg triple). Types without an entry fall back to the
// dot color + 20% alpha compute.
const eventChipTones: Record<string, { bg: string; border: string; fg: string }> = {
  // Device → "moderate" (amber) — bgModerate / separatorModerate / fgModerate.
  // Matches the WATCH pill in Rob's 2026-04-20 reference.
  device: { bg: '#faedd1', border: '#fbd7a0', fg: '#a65006' },
  // Product (asset) → "serious" (red) — bgSerious / separatorSerious /
  // fgSerious. Matches the red badge style in the reference. (Red here
  // isn't signaling an error; it's the chosen tone for product badges.)
  asset: { bg: '#fae6e6', border: '#ffcdc7', fg: '#db1b06' },
};

const eventTypeLabels: Record<string, string> = {
  case: 'Case',
  shipment: 'Shipment',
  order: 'Order',
  device: 'Device',
  asset: 'Product',
};

function EventIcon({ type }: { type: TimelineEvent['type'] }) {
  const Icon =
    type === 'case'
      ? MessageSquare
      : type === 'shipment'
        ? Package
        : type === 'order'
          ? ShoppingCart
          : type === 'asset'
            ? ProductTag
            : Wifi;
  return <Icon size={14} strokeWidth={2.25} color="#fff" />;
}

function buildTimeline(data: MergedCustomer360): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  data.cases.forEach((cs) => {
    events.push({
      id: cs.caseId,
      type: 'case',
      title: cs.title,
      timestamp: cs.createdAt,
      status: cs.status,
      brand: cs.brand,
      summary: cs.summary,
      detail: cs as unknown as Record<string, unknown>,
    });
  });

  const shipmentGroups = new Map<string, ShipmentItem[]>();
  data.shipments.forEach((sh) => {
    const key = String(sh.orderNumber);
    if (!shipmentGroups.has(key)) shipmentGroups.set(key, []);
    shipmentGroups.get(key)!.push(sh);
  });
  shipmentGroups.forEach((items, orderNum) => {
    events.push({
      id: `shipment-${orderNum}`,
      type: 'shipment',
      title: `Shipment #${orderNum} — ${items.length} item${items.length > 1 ? 's' : ''}`,
      timestamp: items[0].orderDate,
      detail: { orderNumber: orderNum, items } as unknown as Record<string, unknown>,
    });
  });

  data.orders.forEach((o) => {
    events.push({
      id: o.orderNumber,
      type: 'order',
      title: `Order ${o.orderNumber}`,
      timestamp: o.date,
      status: o.status,
      detail: o as unknown as Record<string, unknown>,
    });
  });

  data.connectedDevices.forEach((d) => {
    if (d.lastConnected) {
      events.push({
        id: d.controllerSerial,
        type: 'device',
        title: `${d.tradeName} connected`,
        timestamp: d.lastConnected,
        detail: d as unknown as Record<string, unknown>,
      });
    }
  });

  // Products (assets) — anchor each one to its registered date when set,
  // otherwise the install date or last-seen so it still shows up on the
  // timeline. Skip rows with no date at all (rare; would need to land at
  // an arbitrary timestamp and just clutter the view).
  data.assets.forEach((a) => {
    const ts = a.registeredDate ?? a.purchaseDate ?? a.lastConnected ?? null;
    if (!ts) return;
    const titleSuffix = a.nickname ? ` — ${a.nickname}` : '';
    events.push({
      id: a.serialNumber,
      type: 'asset',
      title: `${a.productName || 'Product'} registered${titleSuffix}`,
      timestamp: ts,
      status: a.status,
      detail: a as unknown as Record<string, unknown>,
    });
  });

  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events;
}

type DateRange = 'all' | '7d' | '30d' | '90d' | '1y';
type SortOrder = 'newest' | 'oldest' | 'type';

// Timeline sub-filters — applied on top of the type chips. Case/shipment
// each get an expandable panel so agents can narrow by status or the
// presence of a cross-reference (order #, Loupe link, tracking #) without
// leaving the Timeline view.
type TimelineCaseStatus = 'all' | 'open' | 'closed' | 'pending';
type TimelineShipStatus = 'all' | 'delivered' | 'shipped' | 'pending';
interface TimelineSubFilters {
  caseStatus: TimelineCaseStatus;
  caseHasOrder: boolean;
  caseHasLoupe: boolean;
  shipStatus: TimelineShipStatus;
  shipHasTracking: boolean;
}

const DATE_RANGE_DAYS: Record<DateRange, number | null> = {
  all: null,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
};

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  all: 'All time',
  '7d': 'Last 7d',
  '30d': 'Last 30d',
  '90d': 'Last 90d',
  '1y': 'Last year',
};

function TimelineSection({
  data,
  hideHeader,
  expandedItems,
  toggleItem,
  activeTypes,
  setActiveTypes,
  groupBy,
  setGroupBy,
  collapsedDates,
  setCollapsedDates,
  collapsedTypes,
  setCollapsedTypes,
  subFilters,
  setSubFilters,
  moreFiltersOpen,
  setMoreFiltersOpen,
}: {
  data: MergedCustomer360;
  hideHeader?: boolean;
  expandedItems: Set<string>;
  toggleItem: (key: string) => void;
  /** Controlled from the Panel so sidebar shortcuts (Open Cases, Orders,
   *  Products) can push a filter down. */
  activeTypes: Set<TimelineEvent['type']>;
  setActiveTypes: React.Dispatch<React.SetStateAction<Set<TimelineEvent['type']>>>;
  /** Group-by mode + collapsed-set per mode, lifted to the Panel so that
   *  leaving Timeline (e.g. to check Records) and coming back preserves
   *  the fold state the agent already set up. */
  groupBy: 'date' | 'type';
  setGroupBy: React.Dispatch<React.SetStateAction<'date' | 'type'>>;
  collapsedDates: Set<string>;
  setCollapsedDates: React.Dispatch<React.SetStateAction<Set<string>>>;
  collapsedTypes: Set<string>;
  setCollapsedTypes: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Per-type sub-filter values, lifted so they survive tab switches
   *  (same reason as activeTypes). */
  subFilters: TimelineSubFilters;
  setSubFilters: React.Dispatch<React.SetStateAction<TimelineSubFilters>>;
  /** "More filters" — show/hide the Case / Shipment sub-filter panels. */
  moreFiltersOpen: boolean;
  setMoreFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const allEvents = buildTimeline(data);

  // Translate a timeline event into the same namespaced key the per-type
  // sections use, so expanding a case from Timeline also expands it in
  // the Cases tab (and vice versa).
  const eventToKey = (ev: TimelineEvent): string => {
    switch (ev.type) {
      case 'case':
        return `case:${ev.id}`;
      case 'shipment':
        return `ship:${ev.id.replace(/^shipment-/, '')}`;
      case 'order':
        return `order:${ev.id}`;
      case 'device':
        return `device:${ev.id}`;
      case 'asset':
        return `asset:${ev.id}`;
    }
  };

  // Counts per type — driven from the unfiltered event set so chips don't change
  // count as the user toggles them.
  const typeCounts: Record<TimelineEvent['type'], number> = {
    case: 0,
    shipment: 0,
    order: 0,
    device: 0,
    asset: 0,
  };
  allEvents.forEach((e) => {
    typeCounts[e.type] += 1;
  });

  // Filter state — all-time, no keyword, newest first by default.
  // `activeTypes` + `setActiveTypes` now come in as props so the Panel
  // sidebar can push a filter (e.g. "only cases") when an agent clicks
  // Open Cases / Orders / Products.
  const toggleType = (t: TimelineEvent['type']) =>
    setActiveTypes((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });

  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [keyword, setKeyword] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');

  // Helpers — read the case / shipment fields off of the event's detail
  // blob. Kept inline so the filter chain below reads linearly without a
  // separate module-level export.
  const caseOf = (ev: TimelineEvent) => ev.detail as unknown as CaseItem;
  const shipItemsOf = (ev: TimelineEvent) => (ev.detail as { items?: ShipmentItem[] }).items ?? [];
  // Aggregate a shipment-group event into a single bucket. `delivered`
  // requires every line to be delivered; `shipped` means at least one
  // line left the warehouse but not all have landed; `pending` means
  // nothing has shipped yet. Matches what agents see on the card.
  const shipBucket = (items: ShipmentItem[]): TimelineShipStatus => {
    if (items.length === 0) return 'pending';
    const allDelivered = items.every((it) => it.isDelivered);
    const anyShipped = items.some((it) => !!it.shipDate);
    if (allDelivered) return 'delivered';
    if (anyShipped) return 'shipped';
    return 'pending';
  };

  // Per-status event counts for the sub-filter pill labels. Computed
  // from the full event set (ignoring sub-filters so toggling doesn't
  // make the counts jitter) but scoped to events the type chips would
  // already keep.
  const caseEvents = allEvents.filter((e) => e.type === 'case');
  const caseStatusCounts = {
    all: caseEvents.length,
    open: caseEvents.filter((e) => caseOf(e).status === 'open').length,
    closed: caseEvents.filter((e) => caseOf(e).status === 'closed').length,
    pending: caseEvents.filter((e) => caseOf(e).status === 'pending').length,
  };
  const shipEvents = allEvents.filter((e) => e.type === 'shipment');
  const shipStatusCounts = shipEvents.reduce(
    (acc, e) => {
      const b = shipBucket(shipItemsOf(e));
      acc.all += 1;
      acc[b] += 1;
      return acc;
    },
    { all: 0, delivered: 0, shipped: 0, pending: 0 }
  );

  const events = (() => {
    const cutoff =
      DATE_RANGE_DAYS[dateRange] != null
        ? Date.now() - DATE_RANGE_DAYS[dateRange]! * 86400000
        : null;
    const kw = keyword.trim().toLowerCase();
    const filtered = allEvents.filter((e) => {
      if (!activeTypes.has(e.type)) return false;
      if (cutoff && new Date(e.timestamp).getTime() < cutoff) return false;
      if (kw) {
        const haystack = [e.title, e.summary, e.brand, e.status, e.type]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(kw)) return false;
      }
      // Per-type sub-filters — only gate the type they target so turning
      // on `caseHasLoupe` never hides shipments.
      if (e.type === 'case') {
        const cs = caseOf(e);
        if (subFilters.caseStatus !== 'all' && cs.status !== subFilters.caseStatus) return false;
        if (subFilters.caseHasOrder && !cs.orderNumber) return false;
        if (subFilters.caseHasLoupe && !cs.loupeLink) return false;
      }
      if (e.type === 'shipment') {
        const items = shipItemsOf(e);
        if (subFilters.shipStatus !== 'all' && shipBucket(items) !== subFilters.shipStatus)
          return false;
        if (subFilters.shipHasTracking && !items.some((it) => it.trackingNumber)) return false;
      }
      return true;
    });
    if (sortOrder === 'oldest') {
      filtered.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    } else if (sortOrder === 'type') {
      filtered.sort(
        (a, b) =>
          a.type.localeCompare(b.type) ||
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    }
    return filtered;
  })();

  // groupBy / collapsedDates / collapsedTypes are controlled from the
  // Panel so fold state survives tab switches.

  const grouped = new Map<string, TimelineEvent[]>();
  events.forEach((e) => {
    const key = groupBy === 'date' ? formatDate(e.timestamp) : eventTypeLabels[e.type];
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(e);
  });

  const collapsedSet = groupBy === 'date' ? collapsedDates : collapsedTypes;
  const setCollapsedSet = groupBy === 'date' ? setCollapsedDates : setCollapsedTypes;
  const toggleGroup = (key: string) =>
    setCollapsedSet((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // "Collapse / Expand all" control — computed against the *currently
  // visible* group keys so filtering narrows what the button acts on.
  // Button shows "Expand all" while ANY visible group is still collapsed
  // — only flips to "Collapse all" once every visible group is already
  // open. Same set-of-visible-keys scoping so hidden-group fold state
  // stays untouched.
  const visibleGroupKeys = Array.from(grouped.keys());
  const allExpanded =
    visibleGroupKeys.length > 0 && visibleGroupKeys.every((k) => !collapsedSet.has(k));
  const toggleAll = () => {
    if (allExpanded) {
      setCollapsedSet((prev) => {
        const next = new Set(prev);
        visibleGroupKeys.forEach((k) => next.add(k));
        return next;
      });
    } else {
      setCollapsedSet((prev) => {
        const next = new Set(prev);
        visibleGroupKeys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  const filterTypes: TimelineEvent['type'][] = ['case', 'shipment', 'order', 'device', 'asset'];

  return (
    <Section title="Timeline" count={events.length} hideHeader={hideHeader}>
      <div
        style={{
          padding: '8px 14px',
          borderBottom: `1px solid ${c.borderLight}`,
          background: c.bg,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {(() => {
            // "All events" resets to every visible type; also drives the
            // chips' "highlight only when actually narrowing" rule below.
            // Disabled when every visible chip is already on.
            const visibleTypes = filterTypes.filter((t) => typeCounts[t] > 0);
            const allOn = visibleTypes.length > 0 && visibleTypes.every((t) => activeTypes.has(t));
            return (
              <button
                type="button"
                onClick={() => setActiveTypes(new Set(visibleTypes))}
                disabled={allOn}
                title={allOn ? 'Showing every event type' : 'Show every event type'}
                style={{
                  fontSize: '11px',
                  padding: '4px 10px',
                  borderRadius: '10px',
                  // Highlighted when every type is on (reflects the
                  // current state) and neutral/clickable when narrowed
                  // so clicking obviously restores the full view.
                  border: `1px solid ${allOn ? filterColors.borderActive : filterColors.border}`,
                  background: allOn ? filterColors.bgActive : filterColors.bg,
                  color: allOn ? filterColors.text : filterColors.textMuted,
                  cursor: allOn ? 'default' : 'pointer',
                  fontWeight: 600,
                }}
              >
                All events
              </button>
            );
          })()}
          {(() => {
            const visibleTypes = filterTypes.filter((t) => typeCounts[t] > 0);
            const allOn = visibleTypes.length > 0 && visibleTypes.every((t) => activeTypes.has(t));
            return visibleTypes.map((t) => {
              const isActive = activeTypes.has(t);
              // Drop the colored highlight when every type is on — the
              // "narrowed to this type" cue only makes sense when a
              // narrowing is actually happening. Keep the checkmark
              // regardless so "checked" state stays legible.
              const highlight = isActive && !allOn;
              // Prefer the tonal pill palette (bg / border / fg) when
              // a type has one; otherwise compute from the dot color.
              const tone = eventChipTones[t];
              const borderColor = highlight ? (tone?.border ?? eventDotColors[t]) : c.border;
              const background = highlight ? (tone?.bg ?? eventDotColors[t] + '20') : c.bg;
              const fgColor = highlight ? (tone?.fg ?? eventDotColors[t]) : c.textMuted;
              // Two-part chip:
              //   - Checkbox half (left): toggles this one type in/out
              //     without touching the others (additive).
              //   - Label half (right): exclusively filters to just this
              //     type — quicker than un-checking the other chips one
              //     by one. A vertical divider between the halves makes
              //     the affordance obvious at a glance.
              return (
                <span
                  key={t}
                  className="c360-chip"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'stretch',
                    borderRadius: '10px',
                    // Constant 1px border — color switches to indicate
                    // active, width never changes. box-sizing: border-box
                    // so the border never nudges the chip's total width.
                    border: `1px solid ${borderColor}`,
                    boxSizing: 'border-box',
                    background,
                    overflow: 'hidden',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                >
                  <button
                    type="button"
                    // When every type is on, clicking a checkbox is
                    // effectively "narrow to just this one" — the
                    // additive toggle that subtracts this type reads
                    // strangely from the "All events" state. Once
                    // narrowed, falls back to the usual additive toggle.
                    onClick={() => (allOn ? setActiveTypes(new Set([t])) : toggleType(t))}
                    aria-label={`Toggle ${eventTypeLabels[t]} filter`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 6px',
                      border: 'none',
                      borderRight: `1px solid ${borderColor}`,
                      boxSizing: 'border-box',
                      background: 'transparent',
                      color: fgColor,
                      cursor: 'pointer',
                    }}
                  >
                    {/* Fixed-size slot for the checkbox so active/inactive
                        swaps (checkmark ↔ empty square) never change the
                        chip's width. */}
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isActive ? (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <span
                          style={{
                            width: '10px',
                            height: '10px',
                            boxSizing: 'border-box',
                            borderRadius: '2px',
                            border: `1px solid ${c.textMuted}`,
                          }}
                        />
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTypes(new Set([t]))}
                    title={`Only show ${eventTypeLabels[t]}`}
                    style={{
                      padding: '3px 8px',
                      border: 'none',
                      boxSizing: 'border-box',
                      background: 'transparent',
                      color: fgColor,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {eventTypeLabels[t]}
                    <span style={{ fontWeight: 400, opacity: 0.7 }}>{typeCounts[t]}</span>
                  </button>
                </span>
              );
            });
          })()}
          {/* "More filters" — trailing toggle that reveals the Case /
              Shipment sub-filter panels. Kept inline with the type chips
              so it reads as "extra filter options", not a separate row. */}
          <button
            type="button"
            onClick={() => setMoreFiltersOpen((v) => !v)}
            aria-expanded={moreFiltersOpen}
            title={moreFiltersOpen ? 'Hide more filters' : 'Show more filters'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: '10px',
              border: `1px solid ${filterColors.border}`,
              background: filterColors.bg,
              color: filterColors.textMuted,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            <span>{moreFiltersOpen ? '▾' : '▸'}</span>
            More filters
          </button>
        </div>
        {/* Sub-filter panels — rendered side-by-side, each only shown
            while the corresponding type chip is active. Horizontal gap
            only — when the row wraps, the second panel stacks flush
            against the first (row-gap: 0) to avoid a wasted band of
            whitespace. Entire row hidden unless "More filters" is on. */}
        {moreFiltersOpen && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: '16px',
              rowGap: 0,
            }}
          >
            {activeTypes.has('case') && typeCounts.case > 0 && (
              <TimelineSubFilterPanel label="Case filters" accent={eventDotColors.case}>
                <FilterPillGroup
                  options={[
                    { value: 'all' as const, label: 'All', count: caseStatusCounts.all },
                    { value: 'open' as const, label: 'Open', count: caseStatusCounts.open },
                    { value: 'closed' as const, label: 'Closed', count: caseStatusCounts.closed },
                    ...(caseStatusCounts.pending > 0
                      ? [
                          {
                            value: 'pending' as const,
                            label: 'Pending',
                            count: caseStatusCounts.pending,
                          },
                        ]
                      : []),
                  ]}
                  active={subFilters.caseStatus}
                  onChange={(next) => setSubFilters((v) => ({ ...v, caseStatus: next }))}
                />
                <FilterToggle
                  active={subFilters.caseHasOrder}
                  onClick={() => setSubFilters((v) => ({ ...v, caseHasOrder: !v.caseHasOrder }))}
                >
                  Has order
                </FilterToggle>
                <FilterToggle
                  active={subFilters.caseHasLoupe}
                  onClick={() => setSubFilters((v) => ({ ...v, caseHasLoupe: !v.caseHasLoupe }))}
                >
                  Has Loupe
                </FilterToggle>
              </TimelineSubFilterPanel>
            )}
            {activeTypes.has('shipment') && typeCounts.shipment > 0 && (
              <TimelineSubFilterPanel label="Shipment filters" accent={eventDotColors.shipment}>
                <FilterPillGroup
                  options={[
                    { value: 'all' as const, label: 'All', count: shipStatusCounts.all },
                    {
                      value: 'delivered' as const,
                      label: 'Delivered',
                      count: shipStatusCounts.delivered,
                    },
                    {
                      value: 'shipped' as const,
                      label: 'Shipped',
                      count: shipStatusCounts.shipped,
                    },
                    {
                      value: 'pending' as const,
                      label: 'Pending',
                      count: shipStatusCounts.pending,
                    },
                  ]}
                  active={subFilters.shipStatus}
                  onChange={(next) => setSubFilters((v) => ({ ...v, shipStatus: next }))}
                />
                <FilterToggle
                  active={subFilters.shipHasTracking}
                  onClick={() =>
                    setSubFilters((v) => ({ ...v, shipHasTracking: !v.shipHasTracking }))
                  }
                >
                  Has tracking
                </FilterToggle>
              </TimelineSubFilterPanel>
            )}
          </div>
        )}
        {/* Bottom row — keyword, date range, grouping, sort, and
            expand/collapse-all. Sits below the narrowing controls so
            the view controls read as "post-processing" the result set. */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Filter title, summary…"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{
              flex: '1 1 140px',
              minWidth: '100px',
              fontSize: '11px',
              padding: '4px 8px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              outline: 'none',
            }}
          />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            style={{
              fontSize: '11px',
              padding: '4px 6px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: 'pointer',
            }}
          >
            {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((r) => (
              <option key={r} value={r}>
                {DATE_RANGE_LABELS[r]}
              </option>
            ))}
          </select>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'date' | 'type')}
            title="Group events by date or by activity type"
            style={{
              fontSize: '11px',
              padding: '4px 6px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: 'pointer',
            }}
          >
            <option value="date">Group: Date</option>
            <option value="type">Group: Type</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            style={{
              fontSize: '11px',
              padding: '4px 6px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: 'pointer',
            }}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="type">By type</option>
          </select>
          <button
            type="button"
            onClick={toggleAll}
            disabled={visibleGroupKeys.length === 0}
            title={allExpanded ? 'Collapse every group' : 'Expand every group'}
            style={{
              fontSize: '11px',
              padding: '4px 8px',
              border: `1px solid ${c.border}`,
              borderRadius: '4px',
              background: c.bg,
              color: c.text,
              cursor: visibleGroupKeys.length === 0 ? 'not-allowed' : 'pointer',
              opacity: visibleGroupKeys.length === 0 ? 0.5 : 1,
              fontWeight: 500,
            }}
          >
            {allExpanded ? '▾ Collapse all' : '▸ Expand all'}
          </button>
        </div>
      </div>
      {Array.from(grouped.entries()).map(([groupKey, dateEvents]) => {
        const isCollapsed = collapsedSet.has(groupKey);
        return (
          <div key={groupKey}>
            <div
              className="c360-hover"
              onClick={() => toggleGroup(groupKey)}
              style={{
                padding: '6px 14px',
                fontSize: '10px',
                fontWeight: 600,
                // Switched from textMuted to primary text per c360 notes —
                // gray made the timeline date headers hard to scan.
                color: c.text,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                background: c.bgMuted,
                borderTop: `1px solid ${c.borderLight}`,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                userSelect: 'none',
              }}
            >
              <Chevron open={!isCollapsed} />
              <span>
                {groupKey}
                <span style={{ marginLeft: '6px', fontWeight: 400, color: c.textMuted }}>
                  ({dateEvents.length})
                </span>
              </span>
            </div>
            {!isCollapsed &&
              dateEvents.map((ev) => {
                const itemKey = eventToKey(ev);
                const isOpen = expandedItems.has(itemKey);
                const onToggleEv = () => toggleItem(itemKey);
                const color = eventDotColors[ev.type];
                return (
                  <div
                    key={ev.id}
                    className="c360-hover-row"
                    style={{
                      ...s.itemRow,
                      ...(isOpen ? s.itemRowExpanded : {}),
                      display: 'flex',
                      gap: '8px',
                      position: 'relative',
                    }}
                  >
                    <div
                      onClick={onToggleEv}
                      style={{
                        flexShrink: 0,
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Chevron open={isOpen} />
                    </div>
                    <div style={{ width: '24px', flexShrink: 0, position: 'relative' }}>
                      <div
                        onClick={onToggleEv}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          zIndex: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <EventIcon type={ev.type} />
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          left: '11px',
                          top: '24px',
                          bottom: '-12px',
                          width: '2px',
                          background: c.borderLight,
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0, minHeight: '36px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '8px',
                          cursor: 'pointer',
                        }}
                        onClick={onToggleEv}
                      >
                        <div style={{ ...s.itemTitle, flex: 1, minWidth: 0 }}>{ev.title}</div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexShrink: 0,
                            paddingTop: '2px',
                            fontSize: '10px',
                          }}
                        >
                          {(() => {
                            // Prefer the tonal palette (bg + fg pair)
                            // when defined — ensures badges like Device
                            // aren't low-contrast amber-on-amber.
                            const tone = eventChipTones[ev.type];
                            return (
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  padding: '1px 6px',
                                  borderRadius: '6px',
                                  background: tone?.bg ?? eventDotColors[ev.type] + '20',
                                  color: tone?.fg ?? eventDotColors[ev.type],
                                }}
                              >
                                {eventTypeLabels[ev.type]}
                              </span>
                            );
                          })()}
                          {ev.status && <Badge text={ev.status} />}
                          {ev.brand && <Badge text={`Brand: ${ev.brand}`} />}
                          <span
                            style={{
                              fontSize: '10px',
                              color: c.textMuted,
                              fontFamily: 'monospace',
                            }}
                          >
                            {formatTime(ev.timestamp)}
                          </span>
                        </div>
                      </div>
                      {!isOpen && ev.summary && (
                        <div style={s.itemPreview}>{oneLine(ev.summary)}</div>
                      )}

                      {isOpen && (
                        <div style={s.itemDetail} onClick={(e) => e.stopPropagation()}>
                          {ev.type === 'device' &&
                            typeof ev.detail.latitude === 'number' &&
                            typeof ev.detail.longitude === 'number' &&
                            (() => {
                              const lat = ev.detail.latitude as number;
                              const lng = ev.detail.longitude as number;
                              // Bounding box controls zoom (±1° ≈ state-sized view).
                              const d = 1;
                              const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
                              return (
                                <iframe
                                  title={`Map: ${ev.title}`}
                                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`}
                                  style={{
                                    width: '100%',
                                    height: '180px',
                                    border: 0,
                                    borderRadius: '6px',
                                    marginBottom: '6px',
                                  }}
                                  loading="lazy"
                                  referrerPolicy="no-referrer-when-downgrade"
                                />
                              );
                            })()}
                          {ev.summary && (
                            <CaseSummary
                              source={ev.summary}
                              style={{ marginBottom: '6px', color: '#000000' }}
                            />
                          )}
                          {!!ev.detail.productsOrdered && (
                            <div style={{ marginTop: '4px' }}>
                              <span style={s.detailLabel}>Parts ordered</span>
                              {String(ev.detail.productsOrdered)
                                .split(';')
                                .map((part, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      padding: '1px 0',
                                      color: c.textSecondary,
                                      fontSize: '11px',
                                    }}
                                  >
                                    • {part.trim()}
                                  </div>
                                ))}
                            </div>
                          )}
                          {!!(
                            ev.detail.items &&
                            Array.isArray(ev.detail.items) &&
                            ev.type === 'shipment'
                          ) && (
                            <div>
                              {/* Mirror the Shipments section so status,
                                  destination, carrier and tracking are
                                  visible inline — agents shouldn't have to
                                  jump tabs to see whether an order shipped. */}
                              {(ev.detail.items as ShipmentItem[]).map((item, i) => {
                                const primaryStatus = item.isDelivered
                                  ? 'Delivered'
                                  : item.shipDate
                                    ? 'Shipped'
                                    : 'Pending';
                                const destination = [item.destinationCity, item.destinationState]
                                  .filter(Boolean)
                                  .join(', ');
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      padding: '4px 0',
                                      borderTop: i > 0 ? `1px solid ${c.borderLight}` : 'none',
                                    }}
                                  >
                                    <div style={{ fontSize: '12px' }}>
                                      <span style={{ color: c.text, fontWeight: 500 }}>
                                        {item.productDescription}
                                      </span>
                                      <span
                                        style={{
                                          color: c.textMuted,
                                          marginLeft: '6px',
                                          fontFamily: 'monospace',
                                          fontSize: '10px',
                                        }}
                                      >
                                        {item.productSku}
                                      </span>
                                      {item.priceUsd != null && item.priceUsd > 0 && (
                                        <span style={{ color: c.textSecondary, marginLeft: '6px' }}>
                                          {formatCurrency(item.priceUsd)}
                                        </span>
                                      )}
                                    </div>
                                    <div style={s.itemMeta}>
                                      <Badge text={primaryStatus} />
                                      {item.shipDate && (
                                        <span>Shipped {formatDateTime(item.shipDate)}</span>
                                      )}
                                      {item.deliveredDate && (
                                        <span>Delivered {formatDateTime(item.deliveredDate)}</span>
                                      )}
                                      {item.isDeliveredOnTime === false && <Badge text="Late" />}
                                      {item.daysLate != null && item.daysLate > 0 && (
                                        <span style={{ color: c.red, fontWeight: 600 }}>
                                          {item.daysLate}d late
                                        </span>
                                      )}
                                      {item.appointmentChanges != null &&
                                        item.appointmentChanges > 0 && (
                                          <span>
                                            {item.appointmentChanges} reschedule
                                            {item.appointmentChanges > 1 ? 's' : ''}
                                          </span>
                                        )}
                                      {item.shipperName && <span>{item.shipperName}</span>}
                                      {destination && <span>→ {destination}</span>}
                                      {item.trackingNumber && (
                                        <TrackingLink
                                          shipperName={item.shipperName}
                                          trackingNumber={item.trackingNumber}
                                          style={{ fontFamily: 'monospace' }}
                                        >
                                          {item.trackingNumber}
                                        </TrackingLink>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div style={s.itemProperties}>
                            {!!ev.detail.asset && (
                              <DetailRow label="Product" value={String(ev.detail.asset)} />
                            )}
                            <DetailRow
                              label="Date"
                              value={<DateTimeDisplay value={ev.timestamp} />}
                              copy={ev.timestamp}
                            />
                            {!!ev.detail.referenceNumber && (
                              <DetailRow
                                label="Ref #"
                                value={
                                  ev.type === 'case' ? (
                                    <CaseLink caseId={ev.id}>
                                      {String(ev.detail.referenceNumber)}
                                    </CaseLink>
                                  ) : (
                                    String(ev.detail.referenceNumber)
                                  )
                                }
                                copy={String(ev.detail.referenceNumber)}
                              />
                            )}
                            {ev.type === 'case' && !!ev.detail.agentName && (
                              <DetailRow label="Agent" value={String(ev.detail.agentName)} />
                            )}
                            {ev.type === 'case' && !!ev.detail.bpo && (
                              <DetailRow label="BPO" value={String(ev.detail.bpo)} />
                            )}
                            {ev.type === 'case' && !!ev.detail.site && (
                              <DetailRow label="Site" value={String(ev.detail.site)} />
                            )}
                            {/* Mirror the Devices tab so Timeline → device
                                events show the same set of properties an
                                agent would see by switching to that tab. */}
                            {ev.type === 'device' && !!ev.detail.tradeName && (
                              <DetailRow label="Trade name" value={String(ev.detail.tradeName)} />
                            )}
                            {ev.type === 'device' && !!ev.detail.controllerSerial && (
                              <DetailRow
                                label={ev.detail.identifierKind === 'uuid' ? 'UUID' : 'Serial'}
                                value={String(ev.detail.controllerSerial)}
                                valueStyle={{ fontFamily: 'monospace' }}
                              />
                            )}
                            {ev.type === 'device' && !!ev.detail.firmwareVersion && (
                              <DetailRow
                                label="Firmware"
                                value={String(ev.detail.firmwareVersion)}
                              />
                            )}
                            {ev.type === 'device' && !!ev.detail.ownerName && (
                              <DetailRow label="Owner" value={String(ev.detail.ownerName)} />
                            )}
                            {ev.type === 'device' && !!ev.detail.ownerEmail && (
                              <DetailRow label="Owner email" value={String(ev.detail.ownerEmail)} />
                            )}
                            {ev.type === 'device' && !!ev.detail.fleetEntryDate && (
                              <DetailRow
                                label="In fleet"
                                value={<DateTimeDisplay value={String(ev.detail.fleetEntryDate)} />}
                                copy={String(ev.detail.fleetEntryDate)}
                              />
                            )}
                            {ev.type === 'device' && !!ev.detail.lastConnected && (
                              <DetailRow
                                label="Last connected"
                                value={<DateTimeDisplay value={String(ev.detail.lastConnected)} />}
                                copy={String(ev.detail.lastConnected)}
                              />
                            )}
                            {ev.type === 'device' && !!ev.detail.manufacturedAt && (
                              <DetailRow
                                label="Manufactured"
                                value={<DateTimeDisplay value={String(ev.detail.manufacturedAt)} />}
                                copy={String(ev.detail.manufacturedAt)}
                              />
                            )}
                            {ev.type === 'device' && !!ev.detail.googleMapsLink && (
                              <DetailRow
                                label="Location"
                                value={
                                  <a
                                    href={String(ev.detail.googleMapsLink)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: c.blue, textDecoration: 'underline' }}
                                  >
                                    Open in Google Maps
                                  </a>
                                }
                                copy={String(ev.detail.googleMapsLink)}
                              />
                            )}
                            {/* Product (asset) detail rows. Mirror what the
                                Products tab surfaces so an agent doesn't
                                have to switch tabs to read SKU / serial /
                                model / device UUID for an event in view. */}
                            {ev.type === 'asset' && !!ev.detail.productSku && (
                              <DetailRow
                                label="SKU"
                                value={String(ev.detail.productSku)}
                                valueStyle={{ fontFamily: 'monospace' }}
                              />
                            )}
                            {ev.type === 'asset' && !!ev.detail.serialNumber && (
                              <DetailRow
                                label="Serial"
                                value={String(ev.detail.serialNumber)}
                                valueStyle={{ fontFamily: 'monospace' }}
                              />
                            )}
                            {ev.type === 'asset' && !!ev.detail.deviceUuid &&
                              ev.detail.deviceUuid !== ev.detail.serialNumber && (
                              <DetailRow
                                label="UUID"
                                value={String(ev.detail.deviceUuid)}
                                valueStyle={{ fontFamily: 'monospace' }}
                              />
                            )}
                            {ev.type === 'asset' && !!ev.detail.categoryL1 && (
                              <DetailRow
                                label="Taxonomy"
                                value={[ev.detail.categoryL1, ev.detail.categoryL2, ev.detail.categorySeries, ev.detail.categoryModel]
                                  .filter(Boolean)
                                  .join(' > ')}
                              />
                            )}
                            {ev.type === 'asset' && !!ev.detail.firmwareVersion && (
                              <DetailRow
                                label="Firmware"
                                value={String(ev.detail.firmwareVersion)}
                              />
                            )}
                            {ev.type === 'asset' && !!ev.detail.lastConnected && (
                              <DetailRow
                                label="Last connected"
                                value={<DateTimeDisplay value={String(ev.detail.lastConnected)} />}
                                copy={String(ev.detail.lastConnected)}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </Section>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// Records rolls Cases / Orders / Shipments / Products into a single
// scrollable tab (team feedback: switching tabs to look up each data
// type was getting in the way). Timeline stays on its own because it
// has its own grouping/filter model, and Cooks has its own table shape
// with cook-specific filters.
type RightTab = 'timeline' | 'records' | 'cooks';

export function Customer360Panel({ data: profile }: { data: CustomerProfile }) {
  // Timeline is the default per the c360 UI review on 2026-04-17 — the
  // narrative-first read is what agents said aligns with how they
  // actually work a customer situation.
  const [rightTab, setRightTab] = useState<RightTab>('timeline');
  const [sidebarPosition, setSidebarPosition] = useState<SidebarPosition>(readSidebarPosition);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SIDEBAR_POSITION_STORAGE_KEY, sidebarPosition);
    } catch {
      /* storage unavailable — just live without persistence */
    }
  }, [sidebarPosition]);
  const toggleSidebarPosition = () => setSidebarPosition((p) => (p === 'left' ? 'right' : 'left'));
  const [cookFilter, setCookFilter] = useState<CookFilter>('all');
  const openCooksTab = (f: CookFilter) => {
    setCookFilter(f);
    setRightTab('cooks');
  };
  const data = toViewModel(profile);

  // Lifted item-expansion state — keys are namespaced by section type so
  // a case expanded from the Timeline tab is also expanded in the Cases
  // tab when the user switches over (and any per-section folds survive
  // the unmount/remount that comes with tab switching).
  //   case:<caseId>, ship:<orderNum>, order:<orderNum>,
  //   asset:<serialNumber>, device:<controllerSerial>, cook:<cookId>
  // Everything starts collapsed: the previous "auto-expand when ≤ N"
  // default leaked into the Timeline tab once state was shared, opening
  // device / shipment rows the user didn't ask for. Users expand from
  // wherever they're looking; the state is kept across tabs.
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set());
  const toggleItem = (key: string) =>
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const ensureItemExpanded = (key: string) =>
    setExpandedItems((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  // Controlled open-state for each Records section, so sidebar shortcuts
  // ("Open Cases", "Products (N) ›", product rows, …) can both switch the
  // tab and force the relevant section open without fighting the section's
  // own toggle clicks. Defaults mirror the old uncontrolled behavior —
  // assets/orders start open, cases/shipments collapsed.
  type RecordsSectionKey = 'assets' | 'cases' | 'shipments' | 'orders';
  const [sectionsOpen, setSectionsOpen] = useState<Record<RecordsSectionKey, boolean>>({
    assets: true,
    cases: false,
    shipments: false,
    orders: true,
  });
  const toggleSection = (name: RecordsSectionKey) =>
    setSectionsOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  const openSection = (name: RecordsSectionKey) =>
    setSectionsOpen((prev) => (prev[name] ? prev : { ...prev, [name]: true }));

  // Sidebar handlers for the Records tab (deprecated by the 2026-04-17
  // UI review, kept for callers that haven't been migrated yet).
  const jumpToSection = (name: RecordsSectionKey) => {
    setRightTab('records');
    openSection(name);
  };
  const jumpToItem = (section: RecordsSectionKey, itemKey: string) => {
    jumpToSection(section);
    ensureItemExpanded(itemKey);
  };

  // Timeline filter state lives on the Panel so sidebar shortcuts can
  // push a filter down (e.g. Open Cases → timeline filtered to case
  // events only). Default: every type enabled.
  const [timelineActiveTypes, setTimelineActiveTypes] = useState<Set<TimelineEvent['type']>>(
    () => new Set(['case', 'shipment', 'order', 'device', 'asset'])
  );
  const filterTimelineTo = (types: TimelineEvent['type'][]) => {
    setRightTab('timeline');
    setTimelineActiveTypes(new Set(types));
  };

  // Timeline grouping + fold state — lifted so switching to Records /
  // Cooks and back doesn't wipe the agent's expand/collapse work.
  // collapsedDates is seeded with every date group beyond the first N
  // most-recent ones so the default view still opens focused.
  const [timelineGroupBy, setTimelineGroupBy] = useState<'date' | 'type'>('date');
  const N_DATE_GROUPS_EXPANDED = 3;
  const [timelineCollapsedDates, setTimelineCollapsedDates] = useState<Set<string>>(() => {
    const events = buildTimeline(data);
    const ordered: string[] = [];
    const seen = new Set<string>();
    [...events]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .forEach((e) => {
        const d = formatDate(e.timestamp);
        if (!seen.has(d)) {
          seen.add(d);
          ordered.push(d);
        }
      });
    return new Set(ordered.slice(N_DATE_GROUPS_EXPANDED));
  });
  // In type mode only Devices / Products open by default — the dense
  // case / shipment / order groups start collapsed.
  const [timelineCollapsedTypes, setTimelineCollapsedTypes] = useState<Set<string>>(
    () =>
      new Set(
        (['case', 'shipment', 'order'] as TimelineEvent['type'][]).map((t) => eventTypeLabels[t])
      )
  );

  // Per-type sub-filters (status + attribute toggles) — kept on the Panel
  // so switching tabs preserves the narrowing the agent set up. Defaults
  // are neutral ('all' / false) so the initial Timeline shows everything
  // that matches the type chips.
  const [timelineSubFilters, setTimelineSubFilters] = useState<TimelineSubFilters>({
    caseStatus: 'all',
    caseHasOrder: false,
    caseHasLoupe: false,
    shipStatus: 'all',
    shipHasTracking: false,
  });
  // "More filters" reveals the Case / Shipment sub-filter panels.
  // Collapsed by default — the type chips cover the common path.
  const [timelineMoreFiltersOpen, setTimelineMoreFiltersOpen] = useState(false);

  const id = data.identity;
  const initials = id.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const rightTabs = [
    {
      key: 'timeline' as RightTab,
      label: 'Timeline',
      count:
        data.cases.length +
        data.shipments.length +
        data.orders.length +
        data.connectedDevices.length +
        data.assets.length,
    },
    {
      key: 'records' as RightTab,
      label: 'Records',
      // Combined Cases / Orders / Shipments / Products. Each renders as
      // its own collapsible section under this one tab.
      count: data.cases.length + data.orders.length + data.shipments.length + data.assets.length,
    },
    { key: 'cooks' as RightTab, label: 'Cooks', count: data.cooks.length },
  ].filter((t) => t.key === 'records' || t.key === 'timeline' || t.count > 0);

  return (
    <div
      style={{
        ...s.container,
        // The JSX is written "main content first, sidebar second".
        // row-reverse renders the sidebar on the LEFT, row on the RIGHT.
        flexDirection: sidebarPosition === 'left' ? 'row-reverse' : 'row',
      }}
    >
      <style>{`
        .c360-hover { transition: background 0.12s ease; }
        .c360-hover:hover { background: ${c.bgMuted} !important; }
        .c360-hover-row { transition: background 0.12s ease; }
        .c360-hover-row:hover { background: #eef2f6; }
        .c360-chip { transition: filter 0.12s ease, transform 0.08s ease; }
        .c360-chip:hover:not(:disabled) { filter: brightness(0.94); transform: translateY(-1px); }
        .c360-chip:active:not(:disabled) { transform: translateY(0); }
        .c360-link { transition: background 0.12s ease, text-decoration-color 0.12s ease, text-decoration-thickness 0.12s ease; border-radius: 3px; }
        .c360-link:hover { background: rgba(37, 99, 235, 0.1); text-decoration-color: currentColor; text-decoration-thickness: 2px; }
      `}</style>
      {/* ===== LEFT: Tabbed content =====
          minWidth: 0 is critical — without it this flex child grows to its
          intrinsic content width (long unbreakable case titles, monospace
          IDs, table rows) and pushes the whole layout past the viewport,
          forcing a horizontal scrollbar on the document. */}
      <div
        style={{
          ...s.right,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Alert */}
        {data.alert && (
          <div
            style={{
              ...s.alert,
              ...(alertColors[data.alert.type] || alertColors.info),
              margin: '8px 8px 0',
              flexShrink: 0,
            }}
          >
            {data.alert.type === 'warning' ? '⚠' : data.alert.type === 'error' ? '✕' : 'ℹ'}
            <span>{data.alert.message}</span>
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            borderBottom: `1px solid ${c.border}`,
            background: c.bg,
            flexShrink: 0,
            padding: '0 8px',
          }}
        >
          {rightTabs.map((tab) => (
            <button
              key={tab.key}
              className="c360-hover"
              onClick={() => setRightTab(tab.key)}
              style={{
                padding: '8px 12px',
                fontSize: '11px',
                fontWeight: 600,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: rightTab === tab.key ? c.blue : c.textSecondary,
                borderBottom:
                  rightTab === tab.key ? `2px solid ${c.blue}` : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {tab.label}
              <span style={{ fontSize: '10px', fontWeight: 400, color: c.textMuted }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px', minWidth: 0 }}>
          {rightTab === 'timeline' && (
            <TimelineSection
              data={data}
              hideHeader
              expandedItems={expandedItems}
              toggleItem={toggleItem}
              activeTypes={timelineActiveTypes}
              setActiveTypes={setTimelineActiveTypes}
              groupBy={timelineGroupBy}
              setGroupBy={setTimelineGroupBy}
              collapsedDates={timelineCollapsedDates}
              setCollapsedDates={setTimelineCollapsedDates}
              collapsedTypes={timelineCollapsedTypes}
              setCollapsedTypes={setTimelineCollapsedTypes}
              subFilters={timelineSubFilters}
              setSubFilters={setTimelineSubFilters}
              moreFiltersOpen={timelineMoreFiltersOpen}
              setMoreFiltersOpen={setTimelineMoreFiltersOpen}
            />
          )}
          {rightTab === 'records' && (
            // Sections render with their own collapsible headers (no
            // hideHeader prop) so the combined tab reads like the Search
            // List's stacked layout. Each section keeps its own filter
            // bar and fold state.
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Section order: Products → Cases → Shipments → Orders.
                  Matches the "identify the grill, then the open issue,
                  then how parts are moving" flow agents actually follow.
                  Orders is nearly always empty (viewmodel.orders = [])
                  but stays in case a future backend starts populating it. */}
              {data.assets.length > 0 && (
                <AssetsSection
                  assets={data.assets}
                  expandedItems={expandedItems}
                  toggleItem={toggleItem}
                  isOpen={sectionsOpen.assets}
                  onToggleOpen={() => toggleSection('assets')}
                />
              )}
              {data.cases.length > 0 && (
                <CasesSection
                  cases={data.cases}
                  expandedItems={expandedItems}
                  toggleItem={toggleItem}
                  isOpen={sectionsOpen.cases}
                  onToggleOpen={() => toggleSection('cases')}
                />
              )}
              {data.shipments.length > 0 && (
                <ShipmentsSection
                  shipments={data.shipments}
                  expandedItems={expandedItems}
                  toggleItem={toggleItem}
                  isOpen={sectionsOpen.shipments}
                  onToggleOpen={() => toggleSection('shipments')}
                />
              )}
              {data.orders.length > 0 && (
                <OrdersSection
                  orders={data.orders}
                  expandedItems={expandedItems}
                  toggleItem={toggleItem}
                  isOpen={sectionsOpen.orders}
                  onToggleOpen={() => toggleSection('orders')}
                />
              )}
            </div>
          )}
          {rightTab === 'cooks' && data.cooks.length > 0 && (
            <CooksSection
              cooks={data.cooks}
              filter={cookFilter}
              onFilterChange={setCookFilter}
              hideHeader
              expandedItems={expandedItems}
              toggleItem={toggleItem}
            />
          )}
        </div>
      </div>

      {/* ===== Profile sidebar (position depends on sidebarPosition) ===== */}
      <div
        style={{
          ...s.left,
          // When the sidebar is on the LEFT, the divider belongs on its
          // right edge (abuts main content). When on the RIGHT, flip.
          borderRight: sidebarPosition === 'left' ? `1px solid ${c.border}` : 'none',
          borderLeft: sidebarPosition === 'right' ? `1px solid ${c.border}` : 'none',
        }}
      >
        {/* Profile card */}
        <div style={s.profileCard}>
          <div style={{ ...s.avatarRow, justifyContent: 'space-between' }}>
            <div style={s.avatarRow}>
              <div style={s.avatar}>{initials}</div>
              <div>
                <h2 style={s.profileName}>
                  {id.primaryProfileId ? (
                    <CustomerProfileLink
                      profileId={id.primaryProfileId}
                      style={{ color: 'inherit' }}
                    >
                      {id.name}
                    </CustomerProfileLink>
                  ) : (
                    id.name
                  )}
                </h2>
                <div style={s.profileMeta}>
                  {id.accountType || 'Individual'}
                  {id.accountStatus && (
                    <span
                      style={{
                        marginLeft: '6px',
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: id.accountStatus === 'Active' ? c.green : c.textMuted,
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                  {id.accountStatus && (
                    <span style={{ marginLeft: '4px' }}>{id.accountStatus}</span>
                  )}
                </div>
              </div>
            </div>
            {/* Dock toggle — flips the sidebar between the left and
                right side of the layout. Persisted in localStorage so
                the choice survives reloads. */}
            <button
              type="button"
              onClick={toggleSidebarPosition}
              title={
                sidebarPosition === 'left'
                  ? 'Move sidebar to the right'
                  : 'Move sidebar to the left'
              }
              aria-label="Toggle sidebar side"
              className="c360-hover"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                padding: 0,
                border: `1px solid ${c.border}`,
                borderRadius: '4px',
                background: c.bg,
                color: c.textMuted,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {sidebarPosition === 'left' ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              )}
            </button>
          </div>

          <div style={s.contactList}>
            {id.emails.map((email, i) => (
              <div key={i} style={s.contactItem}>
                <MailIcon />
                <span style={s.contactValue}>{email}</span>
              </div>
            ))}
            {id.phones.map((phone, i) => (
              <div key={i} style={s.contactItem}>
                <PhoneIcon />
                <span style={s.contactValue}>{phone}</span>
              </div>
            ))}
            {id.address && (
              <div style={{ ...s.contactItem, alignItems: 'flex-start' }}>
                <MapPinIcon />
                <div
                  style={{
                    ...s.contactValue,
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.4,
                  }}
                >
                  {id.address}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(id.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      marginTop: '4px',
                      fontSize: '11px',
                      color: c.blue,
                      textDecoration: 'underline',
                    }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional identity attrs surfaced from Connect Customer
            Profiles — Account #, alternate Business Name (when distinct
            from displayed name), Personal Email (when distinct from
            primary). The underlying AC Profile IDs live in the merged
            profiles section below (full ID surfaces on hover). Search
            List shows these under "More details"; the agent-app sidebar
            would otherwise drop them silently. */}
        {(id.accountNumber || id.businessName || id.personalEmail) && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Additional details</div>
            <div style={{ fontSize: '11px', color: c.textSecondary, lineHeight: 1.6 }}>
              {id.accountNumber && (
                <div>
                  <span style={{ color: c.textMuted }}>Account #:</span>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{id.accountNumber}</span>
                </div>
              )}
              {id.businessName && (
                <div>
                  <span style={{ color: c.textMuted }}>Business name:</span> {id.businessName}
                </div>
              )}
              {id.personalEmail && (
                <div>
                  <span style={{ color: c.textMuted }}>Personal email:</span> {id.personalEmail}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Salesforce IDs */}
        {(id.sfAccountId || id.sfContactId) && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Salesforce</div>
            <div
              style={{
                fontSize: '11px',
                color: c.textMuted,
                fontFamily: 'monospace',
                lineHeight: 1.6,
              }}
            >
              {id.sfAccountId && <div>Account: {id.sfAccountId}</div>}
              {id.sfContactId && <div>Contact: {id.sfContactId}</div>}
            </div>
          </div>
        )}

        {/* Quick stats */}
        <div style={s.statsGrid}>
          {data.stats.ytdSales !== undefined && (
            <div style={s.statCell}>
              <div style={s.statValue}>{formatCurrency(data.stats.ytdSales)}</div>
              <div style={s.statLabel}>YTD Sales</div>
            </div>
          )}
          <div
            className="c360-hover"
            role="button"
            title="Filter timeline to cases"
            onClick={() => filterTimelineTo(['case'])}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={{ ...s.statValue, color: data.stats.openCases > 0 ? c.red : c.text }}>
              {data.stats.openCases}
            </div>
            <div style={s.statLabel}>Open Cases</div>
          </div>
          <div
            className="c360-hover"
            role="button"
            title="Filter timeline to shipments"
            // totalOrders is derived from shipment.order_number so the
            // timeline narrows to shipment events.
            onClick={() => filterTimelineTo(['shipment'])}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={s.statValue}>{data.stats.totalOrders}</div>
            <div style={s.statLabel}>Orders</div>
          </div>
          <div
            className="c360-hover"
            role="button"
            title="Filter timeline to products"
            onClick={() => filterTimelineTo(['asset'])}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={s.statValue}>{data.stats.registeredProducts}</div>
            <div style={s.statLabel}>Products</div>
          </div>
        </div>

        {/* Products — assets merged with their connected-device fields, so
            this single section covers what used to be split between the
            Assets and Connected Devices summaries (matches Search List). */}
        {data.assets.length > 0 && (
          <div style={s.mergedSection}>
            <div
              className="c360-hover"
              style={{
                ...s.sectionLabel,
                cursor: 'pointer',
                padding: '4px 6px',
                margin: '-4px -6px 4px',
                borderRadius: '4px',
              }}
              onClick={() => jumpToSection('assets')}
              role="button"
              title="View all products"
            >
              Products ({data.assets.length}) ›
            </div>
            {data.assets.map((a) => {
              // Active / Offline / Unknown — anchored to the IoT
              // last_connected_date rather than SF `status`, since what
              // an agent actually cares about is "did this grill phone
              // home recently?". ≤7d is the same threshold we used in
              // the old DevicesSection. Products with no lastConnected
              // (e.g. SF-only rows that never registered in the Traeger
              // app) render a neutral "Not connected" chip.
              const offlineDays = daysSince(a.lastConnected);
              const activity: {
                label: string;
                dot: string;
                fg: string;
              } = (() => {
                if (offlineDays == null) {
                  return { label: 'Not connected', dot: c.textMuted, fg: c.textSecondary };
                }
                if (offlineDays <= 7) {
                  return { label: 'Active', dot: c.green, fg: '#166534' };
                }
                return {
                  label: `Offline ${offlineDays}d`,
                  dot: c.red,
                  fg: '#991b1b',
                };
              })();
              return (
                <div
                  key={a.serialNumber}
                  className="c360-hover"
                  role="button"
                  title={`Open ${a.productName} in Records`}
                  onClick={() => jumpToItem('assets', `asset:${a.serialNumber}`)}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    padding: '8px 0',
                    borderBottom: `1px solid ${c.borderLight}`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: `${c.amber}15`,
                      color: c.amber,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ProductTag size={18} strokeWidth={2} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: c.text,
                      }}
                    >
                      <span
                        title={activity.label}
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          background: activity.dot,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {a.productName}
                      </span>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: activity.fg,
                          flexShrink: 0,
                        }}
                      >
                        {activity.label}
                      </span>
                    </div>
                    {a.nickname && (
                      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '1px' }}>
                        {a.nickname}
                      </div>
                    )}
                    {/* Secondary metadata rows use textSecondary (darker
                      than muted) so dates/serials/warranty stay legible
                      in the sidebar — the prior gray was too faint. */}
                    {a.serialNumber && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: c.textSecondary,
                          fontFamily: 'monospace',
                          marginTop: '2px',
                        }}
                      >
                        SN: {a.serialNumber}
                      </div>
                    )}
                    {a.deviceUuid && a.deviceUuid !== a.serialNumber && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: c.textSecondary,
                          fontFamily: 'monospace',
                          marginTop: '2px',
                        }}
                      >
                        UUID: {a.deviceUuid}
                      </div>
                    )}
                    {/* Registration anchor — Salesforce `created_date` when
                      the product was registered in SF, or the IoT
                      purchase / fleet-entry dates for orphan-device rows
                      that never got a matching SF asset. */}
                    {(() => {
                      const regDate =
                        a.registeredDate ?? a.purchaseDate ?? a.fleetEntryDate ?? null;
                      if (!regDate) return null;
                      return (
                        <div style={{ fontSize: '10px', color: c.textSecondary, marginTop: '2px' }}>
                          Registered: {timeAgo(regDate)}
                        </div>
                      );
                    })()}
                    {a.warrantyExpiration && (
                      <div style={{ fontSize: '10px', color: c.textSecondary, marginTop: '2px' }}>
                        Warranty:{' '}
                        <span
                          style={{
                            color: a.warrantyVoided ? c.red : a.warrantyPassed ? c.red : c.green,
                            fontWeight: 600,
                          }}
                        >
                          {a.warrantyVoided
                            ? 'Voided'
                            : a.warrantyPassed
                              ? `Expired ${timeAgo(a.warrantyExpiration)}`
                              : `Valid until ${timeAgo(a.warrantyExpiration)}`}
                        </span>
                      </div>
                    )}
                    {a.lastConnected && (
                      <div style={{ fontSize: '10px', color: c.textSecondary, marginTop: '2px' }}>
                        Last seen: {timeAgo(a.lastConnected)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cook analytics */}
        {data.cookSummary && data.cookSummary.totalCooks > 0 && (
          <div style={s.mergedSection}>
            <div
              className="c360-hover"
              style={{
                ...s.sectionLabel,
                cursor: 'pointer',
                padding: '4px 6px',
                margin: '-4px -6px 4px',
                borderRadius: '4px',
              }}
              onClick={() => openCooksTab('all')}
              role="button"
              title="View all cooks"
            >
              Cook analytics (last {data.cookSummary.totalCooks} cooks) ›
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {data.cookSummary.totalCriticalAlerts > 0 && (
                <button
                  className="c360-chip"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCooksTab('critical');
                  }}
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {data.cookSummary.totalCriticalAlerts} critical
                </button>
              )}
              {data.cookSummary.totalWarningAlerts > 0 && (
                <button
                  className="c360-chip"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCooksTab('warnings');
                  }}
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: '#fef3c7',
                    color: '#92400e',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {data.cookSummary.totalWarningAlerts} warnings
                </button>
              )}
              {data.cookSummary.problemCooks > 0 && (
                <button
                  className="c360-chip"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCooksTab('problems');
                  }}
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {data.cookSummary.problemCooks} problem cooks
                </button>
              )}
            </div>
            <div style={{ fontSize: '11px', color: c.textMuted, lineHeight: 1.6 }}>
              {data.cookSummary.runningTotalAllTime != null && (
                <div>
                  Lifetime cooks: <b>{data.cookSummary.runningTotalAllTime}</b>
                </div>
              )}
              {data.cookSummary.avgDurationMinutes != null && (
                <div>
                  Avg duration: <b>{data.cookSummary.avgDurationMinutes} min</b>
                </div>
              )}
              {data.cookSummary.lastCookDate && (
                <div>
                  Last cook: <b>{timeAgo(data.cookSummary.lastCookDate)}</b>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connect profile IDs — always surfaced so the AC Profile ID is
            reachable in the sidebar. Truncated for layout; hover the chip
            to see the full UUID. */}
        {data.sourceProfiles.length > 0 && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>
              {data.sourceProfiles.length === 1
                ? 'Connect profile'
                : `Merged from ${data.sourceProfiles.length} profiles`}
            </div>
            {data.sourceProfiles.map((sp) => (
              <div
                key={sp.profileId}
                className="c360-hover"
                style={{
                  ...s.mergedChip,
                  // align dot + ID strip to the email's first line so a
                  // wrapped (multi-line) email doesn't leave the right
                  // side floating mid-row.
                  alignItems: 'flex-start',
                  borderRadius: '4px',
                  padding: '4px 6px',
                  margin: '0 -6px',
                }}
              >
                <span style={{ ...s.mergedDot, marginTop: '6px' }} />
                <CustomerProfileLink
                  profileId={sp.profileId}
                  title={sp.profileId}
                  style={{
                    flex: 1,
                    // min-width:0 lets the link shrink below its intrinsic
                    // content width, and overflowWrap: anywhere lets long
                    // emails wrap mid-string instead of pushing the ID
                    // strip past the right edge of the chip.
                    minWidth: 0,
                    color: 'inherit',
                    textDecoration: 'none',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {sp.email || sp.phone || '—'}
                </CustomerProfileLink>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    flexShrink: 0,
                  }}
                >
                  <span style={s.mergedId} title={sp.profileId}>
                    {sp.profileId.slice(0, 8)}
                  </span>
                  <CopyButton text={sp.profileId} label="profile ID" />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
